<template>
  <transition name="drawer-slide">
    <div v-if="visible" class="rto-drawer">
      <!-- Header -->
      <div class="rto-drawer-header">
        <div class="rto-drawer-header-title">
          <b-icon icon="gear-fill" class="rto-drawer-header-icon" />
          <span>Configurações RTO</span>
        </div>
        <button class="rto-drawer-close" @click="$emit('close')" title="Fechar">
          <b-icon icon="x-circle" />
        </button>
      </div>

      <!-- Body -->
      <div class="rto-drawer-body">
        <!-- Cards area -->
        <div class="rto-cards">
          <!-- Card: Operacional -->
          <div class="rto-card">
            <button
              class="rto-card-header rto-card-header--toggle"
              @click="toggleSection('operacional')"
              :aria-expanded="openSection === 'operacional'"
            >
              <span class="rto-card-header-left">
                <b-icon icon="gear-fill" class="rto-card-lock-icon" />
                <span>Operacional</span>
              </span>
              <b-icon :icon="openSection === 'operacional' ? 'chevron-up' : 'chevron-down'" class="rto-card-chevron" />
            </button>
            <transition name="card-slide">
            <div v-show="openSection === 'operacional'" class="rto-card-body">
              <!-- Sub-accordion: Objetivo -->
              <div class="rto-sub-accordion">
                <button class="rto-sub-header" @click="isObjectivesOpen = !isObjectivesOpen">
                  <span class="rto-sub-header-left">
                    Objetivo
                    <span v-if="objectives.length > 0" class="rto-sub-badge">{{ objectives.length }}</span>
                  </span>
                  <b-icon :icon="isObjectivesOpen ? 'chevron-up' : 'chevron-down'" />
                </button>
                <transition name="card-slide">
                <div v-show="isObjectivesOpen" class="rto-sub-body">
                  <!-- Empty state -->
                  <div v-if="objectives.length === 0 && !isAddingObjective" class="rto-empty-state">
                    <b-icon icon="clipboard" class="rto-empty-state-icon" />
                    <span class="rto-empty-state-title">Nenhum objetivo</span>
                    <span class="rto-empty-state-subtitle">Adicione um objetivo de otimização para começar</span>
                    <button class="rto-add-dashed-btn rto-add-dashed-btn--empty" @click="isAddingObjective = true">
                      <b-icon icon="plus" class="mr-1" /> Adicionar Objetivo
                    </button>
                  </div>

                  <template v-else>
                    <RtoObjectiveItem
                      v-for="obj in objectives"
                      :key="obj.id"
                      :objective="obj"
                      :isSelected="selectedObjectiveId === obj.id"
                      :triggerDisabled="!hasAvancadaPermission"
                      :hasAvancadaPermission="hasAvancadaPermission"
                      @select="requestSelectObjective(obj)"
                      @update="updateObjective($event)"
                      @remove="removeObjective(obj.id)"
                      @add-gatilho="addGatilho(obj.id)"
                      @open-gatilho="openGatilho(obj.id, $event)"
                      @toggle-gatilho-status="toggleGatilhoStatus(obj.id, $event)"
                      @remove-gatilho="removeGatilho(obj.id, $event)"
                    />

                    <!-- Adicionar Objetivo inline -->
                    <transition name="card-slide">
                      <div v-if="isAddingObjective" class="rto-add-obj-nickname-row">
                        <input
                          v-model="newObjectiveLabel"
                          class="rto-add-obj-input"
                          placeholder="Nome do objetivo..."
                          @keydown.enter.prevent="confirmAddObjective"
                          @keydown.esc="cancelAddObjective"
                        />
                        <button class="rto-add-obj-btn rto-add-obj-btn--confirm" @click="confirmAddObjective">
                          <b-icon icon="check" />
                        </button>
                        <button class="rto-add-obj-btn rto-add-obj-btn--cancel" @click="cancelAddObjective">
                          <b-icon icon="x" />
                        </button>
                      </div>
                    </transition>
                    <transition name="card-slide">
                      <button v-if="!isAddingObjective" class="rto-add-dashed-btn" @click="isAddingObjective = true">
                        <b-icon icon="plus" class="mr-1" /> Adicionar Objetivo
                      </button>
                    </transition>
                  </template>
                </div>
                </transition>
              </div>
            </div>
            </transition>
          </div>
          <!-- Card: Base -->
          <div class="rto-card">
            <button
              class="rto-card-header rto-card-header--toggle"
              @click="toggleSection('base')"
              :aria-expanded="openSection === 'base'"
            >
              <span class="rto-card-header-left">
                <b-icon :icon="hasBasePermission ? 'gear-fill' : 'lock-fill'" class="rto-card-lock-icon" />
                <span>Base</span>
              </span>
              <b-icon :icon="openSection === 'base' ? 'chevron-up' : 'chevron-down'" class="rto-card-chevron" />
            </button>
            <transition name="card-slide">
            <div v-show="openSection === 'base'" class="rto-card-body rto-card-body--relative">
              <div v-if="!hasBasePermission" class="rto-permission-overlay">
                <b-icon icon="lock-fill" class="rto-permission-overlay-icon" />
                <span>Sem permissão para editar parâmetros base</span>
              </div>
              <div class="rto-field">
                <label class="rto-label">
                  Máximo de iterações
                  <span class="rto-label-help" title="Iterações do solver INTERNO do simulador para convergir UMA simulação (balanço de massa/energia da planta) — NÃO é do otimizador. Maior = simulação mais precisa, porém mais lenta. Padrão: 100.">
                    <b-icon icon="question-circle" />
                  </span>
                </label>
                <input type="number" v-model.number="maxIterations" class="rto-input" :class="{ 'rto-input--error': isMaxIterationsInvalid }" />
                <span v-if="isMaxIterationsInvalid" class="rto-field-error">Deve ser um inteiro maior que 0</span>
              </div>

              <div class="rto-field-row">
                <div class="rto-field">
                  <label class="rto-label">Tol. Simulação</label>
                  <input
                    type="number"
                    step="0.0001"
                    v-model.number="tolSimulacao"
                    class="rto-input"
                    :class="{ 'rto-input--error': isTolSimulacaoInvalid }"
                  />
                  <span v-if="isTolSimulacaoInvalid" class="rto-field-error">Maior que 0 e até 1</span>
                </div>
                <div class="rto-field">
                  <label class="rto-label">Tol. Objetivo</label>
                  <input
                    type="number"
                    step="0.1"
                    v-model.number="tolOtimizacao"
                    class="rto-input"
                    :class="{ 'rto-input--error': isTolOtimizacaoInvalid }"
                  />
                  <span v-if="isTolOtimizacaoInvalid" class="rto-field-error">Maior que 0 e até 1</span>
                </div>
              </div>

              <div
                v-if="tolSimulacao > tolOtimizacao && !isTolSimulacaoInvalid && !isTolOtimizacaoInvalid"
                class="rto-alert rto-alert--danger"
              >
                <b-icon icon="exclamation-triangle-fill" class="rto-alert-icon" />
                <span>A tolerância de simulação não deve ser maior que a de objetivo.</span>
              </div>
            </div>
            </transition>
          </div>

          <!-- Card: Avançada -->
          <div class="rto-card">
            <button
              class="rto-card-header rto-card-header--toggle"
              @click="toggleSection('avancada')"
              :aria-expanded="openSection === 'avancada'"
            >
              <span class="rto-card-header-left">
                <b-icon :icon="hasAvancadaPermission ? 'gear-fill' : 'lock-fill'" class="rto-card-lock-icon" />
                <span>Avançada</span>
              </span>
              <b-icon :icon="openSection === 'avancada' ? 'chevron-up' : 'chevron-down'" class="rto-card-chevron" />
            </button>

            <transition name="card-slide">
            <div v-show="openSection === 'avancada'" class="rto-card-body rto-card-body--relative">
              <div v-if="!hasAvancadaPermission" class="rto-permission-overlay">
                <b-icon icon="lock-fill" class="rto-permission-overlay-icon" />
                <span>Sem permissão para editar parâmetros avançados</span>
              </div>
              <!-- Iterações por LM -->
              <div class="rto-field">
                <label class="rto-label">
                  Iterações por LM
                  <span class="rto-label-help" title="Iterações do OTIMIZADOR Levenberg-Marquardt em cada execução: quantos passos ele dá ajustando os setpoints (variáveis manipuladas) para otimizar o objetivo. Cada passo roda uma simulação completa (que por sua vez usa o 'Máximo de iterações'). Maior = busca mais fina, porém mais lenta. Padrão: 35.">
                    <b-icon icon="question-circle" />
                  </span>
                </label>
                <input type="number" v-model.number="maxIteracoesPorLM" class="rto-input" :class="{ 'rto-input--error': isMaxIteracoesPorLMInvalid }" />
                <span v-if="isMaxIteracoesPorLMInvalid" class="rto-field-error">Deve ser um inteiro maior que 0</span>
                <span v-else class="rto-field-hint">Limite por execução do Levenberg-Marquardt (default 35)</span>
              </div>

              <div class="rto-advanced-btns">
                <button class="rto-config-action-btn" @click="showStabilityModal = true">
                  <b-icon icon="gear" class="mr-1" />
                  Critérios de Estabilidade
                  <span v-if="stabilityCriteria.length > 0" class="rto-action-btn-badge">{{ stabilityCriteria.length }}</span>
                </button>
              </div>

              <!-- Configuração Global (admin / Pentagro) -->
              <div v-if="hasGlobalConfigPermission" class="rto-config-global">
                <div class="rto-config-global-header">
                  <b-icon icon="hdd-network" class="rto-config-global-icon" />
                  <span class="rto-config-global-title">Configuração Global</span>
                  <span class="rto-config-global-badge">Admin</span>
                </div>
                <div class="rto-config-global-body">
                  <div v-if="configGlobalLoading" class="rto-config-global-loading">
                    <b-spinner small /> Carregando...
                  </div>
                  <template v-else>
                    <div class="rto-field">
                      <label class="rto-label">IP Primário do Simulador</label>
                      <input v-model="configGlobal.simulatorIp" class="rto-input" placeholder="Ex: 201.23.67.61" />
                    </div>
                    <div class="rto-field">
                      <label class="rto-label">IP Secundário (HA)</label>
                      <input v-model="configGlobal.simulatorIp2" class="rto-input" placeholder="Ex: 201.23.67.62" />
                    </div>
                    <div class="rto-field">
                      <label class="rto-label">Porta TCP do Simulador</label>
                      <input type="number" v-model.number="configGlobal.simulatorPort" class="rto-input" placeholder="Ex: 9000" />
                    </div>
                    <div class="rto-field">
                      <label class="rto-label">Kill Switch (Status Otimizador)</label>
                      <select v-model.number="configGlobal.statusOtimizador" class="rto-input">
                        <option :value="1">Ativo</option>
                        <option :value="2">Inativo</option>
                      </select>
                      <span class="rto-field-hint">Pausa global de novos disparos automáticos. Não cancela execuções em andamento.</span>
                    </div>
                    <div v-if="configGlobalError" class="rto-alert rto-alert--warning">
                      <b-icon icon="exclamation-triangle-fill" class="rto-alert-icon" />
                      <span>{{ configGlobalError }}</span>
                    </div>
                  </template>
                </div>
              </div>

              <!-- Indicadores Influenciados por Objetivo (CA-08.3 - Pentagro) -->
              <!-- Usa o objetivo selecionado no card Operacional (mesma lógica do modal Gatilho). -->
              <div v-if="hasGlobalConfigPermission" class="rto-config-global">
                <div class="rto-config-global-header">
                  <b-icon icon="arrow-up-right-circle" class="rto-config-global-icon" />
                  <span class="rto-config-global-title">Indicadores Influenciados por Objetivo</span>
                  <span class="rto-config-global-badge">Admin</span>
                </div>
                <div class="rto-config-global-body">
                  <div v-if="!selectedObjective" class="rto-alert rto-alert--warning">
                    <b-icon icon="exclamation-triangle-fill" class="rto-alert-icon" />
                    <span>Selecione um objetivo no card "Operacional" para configurar os indicadores influenciados.</span>
                  </div>
                  <div v-else-if="!selectedObjective.rtoOtimizacaoId" class="rto-alert rto-alert--warning">
                    <b-icon icon="exclamation-triangle-fill" class="rto-alert-icon" />
                    <span>O objetivo "{{ selectedObjective.label }}" ainda não foi salvo no RTO. Salve antes de configurar overrides.</span>
                  </div>
                  <template v-else>
                    <div class="rto-field">
                      <label class="rto-label">Objetivo atual</label>
                      <div class="phase3-objective-pill">
                        <b-icon icon="bullseye" />
                        <span>{{ selectedObjective.label }}</span>
                      </div>
                    </div>
                    <div class="rto-field">
                      <label class="rto-label">Overrides configurados</label>
                      <div class="phase3-overrides-summary">
                        <span v-if="phase3OverrideCount.total === 0" class="phase3-no-overrides">
                          Nenhum override — todos os indicadores seguem a regra automática.
                        </span>
                        <template v-else>
                          <span v-if="phase3OverrideCount.include > 0" class="phase3-override-badge phase3-override-badge--include">
                            <b-icon icon="plus-circle-fill" /> {{ phase3OverrideCount.include }} incluído{{ phase3OverrideCount.include > 1 ? 's' : '' }}
                          </span>
                          <span v-if="phase3OverrideCount.exclude > 0" class="phase3-override-badge phase3-override-badge--exclude">
                            <b-icon icon="dash-circle-fill" /> {{ phase3OverrideCount.exclude }} excluído{{ phase3OverrideCount.exclude > 1 ? 's' : '' }}
                          </span>
                        </template>
                      </div>
                      <button class="phase3-open-modal-btn" @click="phase3ModalOpen = true">
                        <b-icon icon="pencil-square" /> Configurar indicadores
                      </button>
                    </div>
                  </template>
                </div>
              </div>

            </div>
            </transition>
          </div>
        </div>
      </div>

      <!-- Modal: Indicadores Influenciados por Objetivo (CA-08.3) -->
      <!-- O body só renderiza com o modal aberto, então a lista de radios não pesa no drawer. -->
      <b-modal
        v-model="phase3ModalOpen"
        centered
        size="lg"
        hide-footer
        header-class="rto-modal-header"
      >
        <template #modal-header>
          <div class="rto-modal-header-content">
            <b-icon icon="arrow-up-right-circle" class="rto-modal-header-icon" />
            <span>
              Indicadores Influenciados — <strong>{{ selectedObjective ? selectedObjective.label : '' }}</strong>
            </span>
          </div>
        </template>
        <template v-if="phase3ModalOpen && selectedObjective && selectedObjective.rtoOtimizacaoId">
          <div class="scm-name-section">
            <span class="rto-field-hint">Auto = segue a regra automática (tag bate). Incluir/Excluir força a marcação.</span>
          </div>
          <div class="rto-field">
            <label class="rto-label">Filtrar indicadores</label>
            <input
              type="text"
              :value="phase3SearchInput"
              @input="onPhase3SearchInput"
              class="rto-input"
              placeholder="Buscar por nome..."
            />
            <span class="rto-field-hint">
              Mostrando {{ filteredPhase3Indicators.length }} de {{ allIndicators.length }} indicadores
            </span>
          </div>
          <div class="phase3-indicators-list">
            <div v-for="ind in filteredPhase3Indicators" :key="ind.key" class="phase3-indicator-row">
              <span class="phase3-indicator-name" :title="ind.indicatorName">{{ ind.indicatorName }}</span>
              <div class="phase3-indicator-radios">
                <label :title="'Auto: segue a regra automática (tag bate)'">
                  <input type="radio" :checked="getOverride(selectedObjective.rtoOtimizacaoId, ind.key) === 'auto'" @change="setOverride(selectedObjective.rtoOtimizacaoId, ind.key, 'auto')" />
                  Auto
                </label>
                <label :title="'Forçar incluir como influenciado'">
                  <input type="radio" :checked="getOverride(selectedObjective.rtoOtimizacaoId, ind.key) === 'include'" @change="setOverride(selectedObjective.rtoOtimizacaoId, ind.key, 'include')" />
                  Incluir
                </label>
                <label :title="'Forçar excluir (não mostra seta)'">
                  <input type="radio" :checked="getOverride(selectedObjective.rtoOtimizacaoId, ind.key) === 'exclude'" @change="setOverride(selectedObjective.rtoOtimizacaoId, ind.key, 'exclude')" />
                  Excluir
                </label>
              </div>
            </div>
            <div v-if="filteredPhase3Indicators.length === 0" class="phase3-empty">
              Nenhum indicador encontrado.
            </div>
          </div>
          <div class="rto-modal-footer">
            <button class="scm-btn scm-btn--cancel scm-btn--close-right" @click="phase3ModalOpen = false">Fechar</button>
          </div>
        </template>
      </b-modal>

      <!-- Modal: Critérios de Estabilidade -->
      <b-modal
        v-model="showStabilityModal"
        centered
        size="md"
        hide-footer
        no-close-on-backdrop
        header-class="rto-modal-header"
      >
        <template #modal-header="{ close }">
          <div class="rto-modal-header-content">
            <div class="rto-modal-header-title">
              <b-icon icon="shield-check" class="rto-modal-header-icon" />
              Critérios de Estabilidade
            </div>
            <button class="rto-modal-header-close" @click="close()" title="Fechar">✕</button>
          </div>
        </template>
        <div class="scm-name-field">
          <label class="scm-name-label">Nome da Estabilidade</label>
          <input v-model="stabilityName" class="rto-input" placeholder="Estabilidade Global" />
        </div>

        <div v-if="stabilityCriteria.length === 0 && !isAddingStabilityCriterion" class="scm-empty-state">
          <b-icon icon="shield-check" class="scm-empty-icon" />
          <span>Nenhum critério adicionado</span>
        </div>

        <div v-if="stabilityCriteria.length > 4" class="scm-search-wrap">
          <b-icon icon="search" class="scm-search-icon" />
          <input v-model="searchStability" class="scm-search-input" placeholder="Filtrar por corrente, propriedade ou atributo..." />
          <button v-if="searchStability" class="scm-search-clear" @click="searchStability = ''">
            <b-icon icon="x" />
          </button>
        </div>

        <transition-group v-if="stabilityCriteria.length > 0" tag="div" name="list-item" class="scm-list">
          <div
            v-for="(criterion, i) in filteredStabilityCriteria"
            :key="criterion.corrente + i"
            class="scm-criterion-row"
            :class="{ 'scm-criterion-row--editing': editingStabilityIndex === stabilityCriteria.indexOf(criterion) }"
            @click="editStabilityCriterion(stabilityCriteria.indexOf(criterion))"
            title="Clique para editar"
          >
            <div class="scm-criterion-info">
              <span class="scm-criterion-tag">{{ criterion.corrente || '—' }}</span>
              <span class="scm-criterion-sep">/</span>
              <span class="scm-criterion-tag">{{ (criterion.propriedade && criterion.propriedade.name) || criterion.propriedade || '—' }}</span>
              <span class="scm-criterion-sep">/</span>
              <span class="scm-criterion-tag">{{ criterion.atributo || '—' }}</span>
            </div>
            <div class="scm-criterion-numbers">
              <span>{{ criterion.numAtualizacoes }} Atualizações</span>
              <span>{{ criterion.variacao }}%</span>
            </div>
            <button class="scm-remove-btn" @click.stop="removeStabilityCriterion(stabilityCriteria.indexOf(criterion))" title="Remover">
              <b-icon icon="trash" />
            </button>
          </div>
        </transition-group>
        <div v-if="stabilityCriteria.length > 0 && filteredStabilityCriteria.length === 0" class="scm-search-empty">
          Nenhum critério encontrado para "{{ searchStability }}"
        </div>

        <transition name="card-slide">
          <div v-if="isAddingStabilityCriterion" class="scm-add-form">
            <div v-if="!singletonLoaded" class="scm-singleton-warning">
              <b-icon icon="exclamation-triangle-fill" class="mr-1" />
              Execute uma simulação para carregar as correntes.
            </div>
            <div class="scm-add-field scm-add-field--full">
              <label class="scm-add-label">Corrente</label>
              <SearchableSelect
                :modelRef="newStabilityCriterion.corrente"
                :optList="correnteList"
                optValue="name"
                optDisplay="name"
                :inputFunc="setStabilityCorrente"
                placeholder="Selecione uma corrente..."
                :clearable="true"
                extraClass="scm-searchable"
              />
              <span v-if="stabilitySubmitAttempted && !newStabilityCriterion.corrente" class="scm-field-error">Campo obrigatório</span>
            </div>
            <div v-if="newStabilityCriterion.corrente" class="scm-add-field scm-add-field--full">
              <label class="scm-add-label">Propriedade</label>
              <SearchableSelect
                :modelRef="newStabilityCriterion.propriedade ? newStabilityCriterion.propriedade.name : null"
                :optList="stabilityPropriedadeList"
                optValue="name"
                optDisplay="name"
                :inputFunc="setStabilityPropriedade"
                placeholder="Selecione uma propriedade..."
                :clearable="true"
                extraClass="scm-searchable"
              />
              <span v-if="stabilitySubmitAttempted && !newStabilityCriterion.propriedade" class="scm-field-error">Campo obrigatório</span>
            </div>
            <div v-if="newStabilityCriterion.propriedade" class="scm-add-field scm-add-field--full">
              <label class="scm-add-label">Atributo</label>
              <SearchableSelect
                :modelRef="newStabilityCriterion.atributo"
                :optList="stabilityAtributoList"
                optValue="key"
                optDisplay="key"
                :inputFunc="setStabilityAtributo"
                placeholder="Selecione um atributo..."
                :clearable="true"
                extraClass="scm-searchable"
              />
              <span v-if="stabilitySubmitAttempted && !newStabilityCriterion.atributo" class="scm-field-error">Campo obrigatório</span>
            </div>
            <div class="scm-add-row scm-add-row--2col">
              <div class="scm-add-field">
                <label class="scm-add-label">N° de Atualizações</label>
                <input type="number" min="1" v-model.number="newStabilityCriterion.numAtualizacoes" class="rto-input" placeholder="Ex: 5" />
                <span v-if="stabilitySubmitAttempted && !newStabilityCriterion.numAtualizacoes" class="scm-field-error">Campo obrigatório</span>
              </div>
              <div class="scm-add-field">
                <label class="scm-add-label">Variação (%)</label>
                <input type="number" min="0" step="0.1" v-model.number="newStabilityCriterion.variacao" class="rto-input" placeholder="Ex: 2.5" />
                <span v-if="stabilitySubmitAttempted && newStabilityCriterion.variacao === null" class="scm-field-error">Campo obrigatório</span>
              </div>
            </div>
            <div class="scm-add-row scm-add-row--2col">
              <div class="scm-add-field">
                <label class="scm-add-label">Mínimo (filtro outliers)</label>
                <input type="number" v-model.number="newStabilityCriterion.min" class="rto-input" placeholder="0" />
              </div>
              <div class="scm-add-field">
                <label class="scm-add-label">Máximo (filtro outliers)</label>
                <input type="number" v-model.number="newStabilityCriterion.max" class="rto-input" placeholder="1000000" />
              </div>
            </div>
            <div v-if="newStabilityCriterion.numAtualizacoes > 0" class="scm-coef-display">
              <span class="scm-coef-label">Coeficiente Angular (Tendência)</span>
              <span class="scm-coef-value">{{ (newStabilityCriterion.variacao / newStabilityCriterion.numAtualizacoes).toFixed(4) }}</span>
              <span class="scm-coef-unit">% / ponto</span>
            </div>
            <div class="scm-add-actions">
              <button class="scm-btn scm-btn--confirm" @click="confirmAddStabilityCriterion">
                <b-icon icon="check" class="mr-1" /> Confirmar
              </button>
              <button class="scm-btn scm-btn--cancel" @click="cancelAddStabilityCriterion">
                Cancelar
              </button>
            </div>
          </div>
        </transition>

        <button v-if="!isAddingStabilityCriterion" class="scm-add-btn" @click="isAddingStabilityCriterion = true">
          <b-icon icon="plus" class="mr-1" /> Adicionar
        </button>

        <div class="scm-footer">
          <button v-if="stabilityCriteria.length > 0" class="scm-btn scm-btn--delete-all" @click="deleteAll('stability')">
            <b-icon icon="trash" class="mr-1" /> Excluir todos
          </button>
          <button class="scm-btn scm-btn--cancel scm-btn--close-right" @click="showStabilityModal = false">Fechar</button>
        </div>
      </b-modal>

      <!-- Modal: Confirmar ativação do objetivo como otimização ATIVA do RTO -->
      <b-modal
        v-model="showActivateConfirm"
        centered
        size="md"
        hide-footer
        no-close-on-backdrop
        header-class="rto-modal-header"
      >
        <template #modal-header="{ close }">
          <div class="rto-modal-header-content">
            <div class="rto-modal-header-title">
              <b-icon icon="lightning-charge" class="rto-modal-header-icon" />
              Ativar otimização do RTO
            </div>
            <button class="rto-modal-header-close" @click="close()" title="Fechar">✕</button>
          </div>
        </template>
        <div class="rto-activate-body">
          <p>
            Tornar <strong>{{ pendingActivateObj ? pendingActivateObj.label : '' }}</strong>
            a otimização <strong>ativa</strong> do RTO?
          </p>
          <p class="rto-activate-warning">
            <b-icon icon="exclamation-triangle" class="mr-1" />
            As outras otimizações serão <strong>desativadas automaticamente</strong> (só 1 ativa por vez).
          </p>
        </div>
        <div class="scm-footer">
          <button
            class="scm-btn scm-btn--cancel"
            :disabled="activating"
            @click="showActivateConfirm = false; pendingActivateObj = null"
          >
            Cancelar
          </button>
          <button
            class="scm-btn scm-btn--confirm scm-btn--close-right"
            :disabled="activating"
            @click="confirmActivateObjective"
          >
            {{ activating ? 'Ativando…' : 'Ativar' }}
          </button>
        </div>
      </b-modal>

      <!-- Modal: Gatilho para atuação do RTO -->
      <b-modal
        v-model="showTriggerModal"
        centered
        size="md"
        hide-footer
        no-close-on-backdrop
        header-class="rto-modal-header"
      >
        <template #modal-header="{ close }">
          <div class="rto-modal-header-content">
            <div class="rto-modal-header-title">
              <b-icon icon="lightning" class="rto-modal-header-icon" />
              Gatilho para atuação do RTO
            </div>
            <button class="rto-modal-header-close" @click="close()" title="Fechar">✕</button>
          </div>
        </template>
        <template v-if="editingGatilho">
        <div class="scm-name-field">
          <label class="scm-name-label">Nome do Gatilho</label>
          <input v-model="selectedTriggerName" class="rto-input" placeholder="GTL-Maximização Mix" />
        </div>

        <div v-if="selectedTriggers.length === 0 && !isAddingTrigger" class="scm-empty-state">
          <b-icon icon="lightning" class="scm-empty-icon" />
          <span>Nenhum elemento adicionado</span>
        </div>

        <div v-if="selectedTriggers.length > 4" class="scm-search-wrap">
          <b-icon icon="search" class="scm-search-icon" />
          <input v-model="searchTrigger" class="scm-search-input" placeholder="Filtrar por corrente, propriedade ou atributo..." />
          <button v-if="searchTrigger" class="scm-search-clear" @click="searchTrigger = ''">
            <b-icon icon="x" />
          </button>
        </div>

        <transition-group v-if="selectedTriggers.length > 0" tag="div" name="list-item" class="scm-list">
          <div
            v-for="(trigger, i) in filteredTriggers"
            :key="trigger.corrente + i"
            class="scm-criterion-row"
            :class="{ 'scm-criterion-row--editing': editingTriggerIndex === selectedTriggers.indexOf(trigger) }"
            @click="editTrigger(selectedTriggers.indexOf(trigger))"
            title="Clique para editar"
          >
            <div class="scm-criterion-info">
              <span class="scm-criterion-tag">{{ trigger.corrente || '—' }}</span>
              <span class="scm-criterion-sep">/</span>
              <span class="scm-criterion-tag">{{ (trigger.propriedade && trigger.propriedade.name) || trigger.propriedade || '—' }}</span>
              <span class="scm-criterion-sep">/</span>
              <span class="scm-criterion-tag">{{ trigger.atributo || '—' }}</span>
            </div>
            <div class="scm-criterion-numbers">
              <span>{{ trigger.numAtualizacoes }} Atualizações</span>
              <span>{{ trigger.variacao }}%</span>
              <span v-if="trigger.pausado" class="scm-pausado-chip" title="Pausado">⏸</span>
            </div>
            <button class="scm-remove-btn" @click.stop="removeTrigger(selectedTriggers.indexOf(trigger))" title="Remover">
              <b-icon icon="trash" />
            </button>
          </div>
        </transition-group>
        <div v-if="selectedTriggers.length > 0 && filteredTriggers.length === 0" class="scm-search-empty">
          Nenhum elemento encontrado para "{{ searchTrigger }}"
        </div>

        <transition name="card-slide">
          <div v-if="isAddingTrigger" class="scm-add-form">
            <div v-if="!singletonLoaded" class="scm-singleton-warning">
              <b-icon icon="exclamation-triangle-fill" class="mr-1" />
              Execute uma simulação para carregar as correntes.
            </div>
            <div class="scm-add-field scm-add-field--full">
              <label class="scm-add-label">Corrente</label>
              <SearchableSelect
                :modelRef="newTrigger.corrente"
                :optList="correnteList"
                optValue="name"
                optDisplay="name"
                :inputFunc="setTriggerCorrente"
                placeholder="Selecione uma corrente..."
                :clearable="true"
                extraClass="scm-searchable"
              />
              <span v-if="triggerSubmitAttempted && !newTrigger.corrente" class="scm-field-error">Campo obrigatório</span>
            </div>
            <div v-if="newTrigger.corrente" class="scm-add-field scm-add-field--full">
              <label class="scm-add-label">Propriedade</label>
              <SearchableSelect
                :modelRef="newTrigger.propriedade ? newTrigger.propriedade.name : null"
                :optList="triggerPropriedadeList"
                optValue="name"
                optDisplay="name"
                :inputFunc="setTriggerPropriedade"
                placeholder="Selecione uma propriedade..."
                :clearable="true"
                extraClass="scm-searchable"
              />
              <span v-if="triggerSubmitAttempted && !newTrigger.propriedade" class="scm-field-error">Campo obrigatório</span>
            </div>
            <div v-if="newTrigger.propriedade" class="scm-add-field scm-add-field--full">
              <label class="scm-add-label">Atributo</label>
              <SearchableSelect
                :modelRef="newTrigger.atributo"
                :optList="triggerAtributoList"
                optValue="key"
                optDisplay="key"
                :inputFunc="setTriggerAtributo"
                placeholder="Selecione um atributo..."
                :clearable="true"
                extraClass="scm-searchable"
              />
              <span v-if="triggerSubmitAttempted && !newTrigger.atributo" class="scm-field-error">Campo obrigatório</span>
            </div>
            <div class="scm-add-row scm-add-row--2col">
              <div class="scm-add-field">
                <label class="scm-add-label">N° de Atualizações</label>
                <input type="number" min="2" v-model.number="newTrigger.numAtualizacoes" class="rto-input" placeholder="Ex: 5" />
                <span v-if="newTrigger.numAtualizacoes && newTrigger.numAtualizacoes < 2" class="scm-field-error">Mínimo de 2 atualizações</span>
                <span v-else-if="triggerSubmitAttempted && !newTrigger.numAtualizacoes" class="scm-field-error">Campo obrigatório</span>
              </div>
              <div class="scm-add-field">
                <label class="scm-add-label">Variação (%)</label>
                <input type="number" min="0" step="0.1" v-model.number="newTrigger.variacao" class="rto-input" placeholder="Ex: 2.5" />
                <span v-if="triggerSubmitAttempted && newTrigger.variacao === null" class="scm-field-error">Campo obrigatório</span>
              </div>
            </div>
            <div class="scm-add-row scm-add-row--2col">
              <div class="scm-add-field">
                <label class="scm-add-label">Mínimo (filtro outliers)</label>
                <input type="number" v-model.number="newTrigger.min" class="rto-input" placeholder="0" />
              </div>
              <div class="scm-add-field">
                <label class="scm-add-label">Máximo (filtro outliers)</label>
                <input type="number" v-model.number="newTrigger.max" class="rto-input" placeholder="1000000" />
              </div>
            </div>
            <div class="scm-add-row scm-add-row--2col">
              <div class="scm-add-field">
                <label class="scm-add-label">Valores de Espera (cooldown)</label>
                <input type="number" min="0" v-model.number="newTrigger.valoresEspera" class="rto-input" placeholder="3" />
              </div>
              <div class="scm-add-field">
                <label
                  class="scm-toggle-inline scm-toggle-pause"
                  :class="{ 'scm-toggle-pause--on': newTrigger.pausado }"
                  title="Quando ligado, este gatilho é ignorado: o RTO NÃO dispara a otimização por ele neste objetivo. Use para desativar temporariamente sem precisar excluir o elemento."
                >
                  <input type="checkbox" v-model="newTrigger.pausado" />
                  <b-icon :icon="newTrigger.pausado ? 'pause-circle-fill' : 'pause-circle'" class="scm-toggle-pause-icon" />
                  <span>Pausar atuação deste gatilho</span>
                  <b-icon icon="question-circle" class="scm-toggle-pause-help" />
                </label>
                <small v-if="newTrigger.pausado" class="scm-toggle-pause-hint">
                  Ignorado: não dispara a atuação do RTO neste objetivo.
                </small>
              </div>
            </div>
            <div v-if="newTrigger.numAtualizacoes > 0" class="scm-coef-display">
              <span class="scm-coef-label">Coeficiente Angular (Tendência)</span>
              <span class="scm-coef-value">{{ (newTrigger.variacao / newTrigger.numAtualizacoes).toFixed(4) }}</span>
              <span class="scm-coef-unit">% / ponto</span>
            </div>
            <div class="scm-add-actions">
              <button
                class="scm-btn scm-btn--confirm"
                :disabled="!(newTrigger.numAtualizacoes >= 2)"
                @click="confirmAddTrigger"
              >
                <b-icon icon="check" class="mr-1" /> Confirmar
              </button>
              <button class="scm-btn scm-btn--cancel" @click="cancelAddTrigger">
                Cancelar
              </button>
            </div>
          </div>
        </transition>

        <button v-if="!isAddingTrigger" class="scm-add-btn" @click="isAddingTrigger = true">
          <b-icon icon="plus" class="mr-1" /> Adicionar
        </button>

        <div class="scm-footer">
          <button v-if="selectedTriggers.length > 0" class="scm-btn scm-btn--delete-all" @click="deleteAll('trigger')">
            <b-icon icon="trash" class="mr-1" /> Excluir todos
          </button>
          <button class="scm-btn scm-btn--cancel scm-btn--close-right" @click="showTriggerModal = false">Fechar</button>
        </div>
        </template>
      </b-modal>

      <!-- Footer -->
      <div class="rto-drawer-footer">
        <div class="rto-footer-row">
          <button class="rto-footer-btn" @click="importJson">
            <b-icon icon="upload" class="mr-1" />
            Importar JSON
          </button>
          <button class="rto-footer-btn" @click="exportJson">
            <b-icon icon="download" class="mr-1" />
            Exportar JSON
          </button>
          <input ref="fileInput" type="file" accept=".json" style="display:none" @change="onFileSelected" />
        </div>
        <div v-if="showSaveIssues && selectedObjectiveValidationIssues.length > 0" class="rto-save-issues">
          <div class="rto-save-issues-header">
            <b-icon icon="exclamation-triangle-fill" class="rto-save-issues-icon" />
            <strong>{{ selectedObjectiveValidationIssues.length === 1 ? 'Pendência para salvar:' : `${selectedObjectiveValidationIssues.length} pendências para salvar:` }}</strong>
          </div>
          <ul class="rto-save-issues-list">
            <li v-for="(issue, i) in selectedObjectiveValidationIssues" :key="i">{{ issue }}</li>
          </ul>
        </div>
        <button
          class="rto-footer-save-btn"
          :class="{ 'rto-footer-save-btn--saved': savedSuccess }"
          :disabled="isSaveDisabled || isSaving"
          @click="save"
        >
          <b-spinner v-if="isSaving" small class="mr-1" />
          <b-icon v-else-if="savedSuccess" icon="check-circle-fill" class="mr-1" />
          {{ isSaving ? 'Salvando...' : savedSuccess ? 'Salvo' : 'Salvar' }}
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
import RtoObjectiveItem from './RtoObjectiveItem.vue';
import SearchableSelect from '@/components/rta/commons/SearchableSelect.vue';
import { SingletonEnginnering } from '@/components/flowchart/nodes/_service/singletonEngineering';
import EngineeringMapping from '@/components/flowchart/nodes/_service/singletonTools/engineeringMapping';
import axios from 'axios';
import { mapState } from 'vuex';
import { urlMainAPI, defaultSuccess, showError } from '@/global';
import { isRtoUnreachableError, RTO_UNREACHABLE_MESSAGE } from './rtoErrors';
import { toRtoScale, fromRtoScale } from './rtoScale';
import { safeRandomUUID } from '@/services/uuid';

export default {
  name: 'RtoConfigDrawer',

  components: { RtoObjectiveItem, SearchableSelect },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close'],

  data() {
    return {
      openSection: null,
      configLoaded: false,
      // Base
      maxIterations: 100,
      tolSimulacao: 0.0001,
      tolOtimizacao: 0.1,
      // Avançada (solver) — usarGrid/tamanhoGrid/multiStartK passaram a viver por objetivo
      maxIteracoesPorLM: 35,
      // Configuração Global (admin/Pentagro)
      configGlobal: { simulatorIp: '', simulatorIp2: '', simulatorPort: null, statusOtimizador: 1 },
      configGlobalLoading: false,
      configGlobalError: null,
      // CA-08.3: overrides "incluir/excluir indicador por objetivo" (Pentagro)
      // Estrutura: { "<rtoOtimizacaoId>": { "<indicatorKey>": "include" | "exclude" } }
      // O "objetivo atual" vem do radio selecionado no card Operacional (selectedObjective).
      rtoIndicatorOverrides: {},
      allIndicators: [],
      // phase3SearchInput é o que o usuário digita (atualiza a cada tecla).
      // phase3SearchTerm é o termo "debounced" que dispara o filtro — evita re-render dos
      // 200+ items a cada keystroke, que estava engolindo letras.
      phase3SearchInput: '',
      phase3SearchTerm: '',
      phase3SearchDebounce: null,
      phase3ModalOpen: false,
      // Operacional — Objetivo
      isObjectivesOpen: false,
      selectedObjectiveId: null,
      objectives: [],
      isAddingObjective: false,
      newObjectiveLabel: '',
      // Modal de confirmação de ativação (selecionar objetivo = tornar a otimização ativa no RTO)
      showActivateConfirm: false,
      pendingActivateObj: null,
      activating: false,
      isSaving: false,
      showSaveIssues: false,
      savedSuccess: false,
      // Critérios de Estabilidade
      showStabilityModal: false,
      stabilityName: 'Estabilidade Global',
      stabilityCriteria: [],
      isAddingStabilityCriterion: false,
      editingStabilityIndex: null,
      newStabilityCriterion: { corrente: null, propriedade: null, atributo: null, numAtualizacoes: null, variacao: null, min: 0, max: 1000000 },
      // Pesquisa nos modais
      searchStability: '',
      searchTrigger: '',
      stabilitySubmitAttempted: false,
      triggerSubmitAttempted: false,
      // Gatilhos para atuação do RTO — cada objetivo tem N gatilhos (obj.gatilhos[]).
      // O modal edita UM gatilho de cada vez; "trigger" aqui = elemento de um gatilho.
      showTriggerModal: false,
      editingObjectiveId: null,
      editingGatilhoId: null,
      // Gatilhos que existiam no RTO e foram removidos na UI — excluídos no save().
      removedGatilhos: [],
      isAddingTrigger: false,
      editingTriggerIndex: null,
      newTrigger: { corrente: null, propriedade: null, atributo: null, numAtualizacoes: null, variacao: null, min: 0, max: 1000000, valoresEspera: 3, pausado: false },
    };
  },

  computed: {
    ...mapState(['user']),

    hasBasePermission() {
      return this.user && this.user.Permissions?.rtoconfigbase?.allowed;
    },

    hasAvancadaPermission() {
      return this.user && this.user.Permissions?.rtoconfigavancada?.allowed;
    },

    hasGlobalConfigPermission() {
      // TODO: criar permissão dedicada rtoconfigglobal no backend.
      // Por ora reutiliza a permissão da seção Avançada (igualmente restrita).
      return this.user && this.user.Permissions?.rtoconfigavancada?.allowed;
    },

    filteredPhase3Indicators() {
      const term = (this.phase3SearchTerm || '').trim().toLowerCase();
      const out = [];
      for (const i of this.allIndicators) {
        if (term && !(i.indicatorName && i.indicatorName.toLowerCase().includes(term))) continue;
        out.push({
          key: `${i.indicatorId}-${i.productionUnitId}-${i.system}`,
          indicatorName: i.indicatorName,
        });
      }
      return out;
    },

    phase3OverrideCount() {
      if (!this.selectedObjective?.rtoOtimizacaoId) return { include: 0, exclude: 0, total: 0 };
      const perObj = this.rtoIndicatorOverrides[String(this.selectedObjective.rtoOtimizacaoId)] || {};
      let inc = 0, exc = 0;
      for (const k of Object.keys(perObj)) {
        if (perObj[k] === 'include') inc++;
        else if (perObj[k] === 'exclude') exc++;
      }
      return { include: inc, exclude: exc, total: inc + exc };
    },

    selectedObjective() {
      return this.objectives.find(o => o.id === this.selectedObjectiveId);
    },

    // Gatilho aberto no modal (referência ao item dentro de obj.gatilhos).
    editingGatilho() {
      const obj = this.objectives.find(o => o.id === this.editingObjectiveId);
      if (!obj) return null;
      return (obj.gatilhos || []).find(g => g.id === this.editingGatilhoId) || null;
    },

    selectedTriggerName: {
      get() { return this.editingGatilho?.nome ?? ''; },
      set(v) { this.patchEditingGatilho({ nome: v }); },
    },

    // Elementos do gatilho em edição (cada elemento = uma tag monitorada).
    selectedTriggers() {
      return this.editingGatilho?.elementos ?? [];
    },

    isTolSimulacaoInvalid() {
      return this.tolSimulacao == null || this.tolSimulacao <= 0 || this.tolSimulacao > 1;
    },

    isTolOtimizacaoInvalid() {
      return this.tolOtimizacao == null || this.tolOtimizacao <= 0 || this.tolOtimizacao > 1;
    },

    isMaxIterationsInvalid() {
      return !this.maxIterations || this.maxIterations < 1 || !Number.isInteger(this.maxIterations);
    },

    isMaxIteracoesPorLMInvalid() {
      return !this.maxIteracoesPorLM || this.maxIteracoesPorLM < 1 || !Number.isInteger(this.maxIteracoesPorLM);
    },

    selectedObjectiveValidationIssues() {
      const obj = this.objectives.find(o => o.id === this.selectedObjectiveId);
      return this.getObjectiveValidationIssues(obj);
    },

    isSaveDisabled() {
      return this.isTolSimulacaoInvalid || this.isTolOtimizacaoInvalid ||
             this.isMaxIterationsInvalid ||
             this.isMaxIteracoesPorLMInvalid ||
             (!this.isTolSimulacaoInvalid && !this.isTolOtimizacaoInvalid && this.tolSimulacao > this.tolOtimizacao);
    },

    singletonLoaded() {
      return SingletonEnginnering.isLoaded && !!SingletonEnginnering.currents;
    },

    correnteList() {
      if (!this.singletonLoaded) return [];
      return SingletonEnginnering.currents.map(c => ({ name: c }));
    },

    filteredStabilityCriteria() {
      if (!this.searchStability) return this.stabilityCriteria;
      const q = this.searchStability.toLowerCase();
      return this.stabilityCriteria.filter(c =>
        (c.corrente || '').toLowerCase().includes(q) ||
        ((c.propriedade && c.propriedade.name) || c.propriedade || '').toLowerCase().includes(q) ||
        (c.atributo || '').toLowerCase().includes(q)
      );
    },

    filteredTriggers() {
      const triggers = this.selectedTriggers;
      if (!this.searchTrigger) return triggers;
      const q = this.searchTrigger.toLowerCase();
      return triggers.filter(t =>
        (t.corrente || '').toLowerCase().includes(q) ||
        ((t.propriedade && t.propriedade.name) || t.propriedade || '').toLowerCase().includes(q) ||
        (t.atributo || '').toLowerCase().includes(q)
      );
    },

    // Cascata — Critérios de Estabilidade
    stabilityPropriedadeList() {
      if (!this.newStabilityCriterion.corrente) return [];
      try { return SingletonEnginnering.getCurrentProperties(this.newStabilityCriterion.corrente); }
      catch { return []; }
    },
    stabilityAtributoList() {
      if (!this.newStabilityCriterion.corrente || !this.newStabilityCriterion.propriedade) return [];
      try { return SingletonEnginnering.getCurrentPropertiesAttributes(this.newStabilityCriterion.corrente, this.newStabilityCriterion.propriedade); }
      catch { return []; }
    },

    // Cascata — Gatilhos
    triggerPropriedadeList() {
      if (!this.newTrigger.corrente) return [];
      try { return SingletonEnginnering.getCurrentProperties(this.newTrigger.corrente); }
      catch { return []; }
    },
    triggerAtributoList() {
      if (!this.newTrigger.corrente || !this.newTrigger.propriedade) return [];
      try { return SingletonEnginnering.getCurrentPropertiesAttributes(this.newTrigger.corrente, this.newTrigger.propriedade); }
      catch { return []; }
    },
  },

  watch: {
    showStabilityModal(val) {
      if (!val) this.searchStability = '';
    },
    showTriggerModal(val) {
      if (!val) this.searchTrigger = '';
    },
    selectedObjectiveId() {
      // Ao trocar de objetivo, reset do form de adição/edição do modal Gatilho
      this.cancelAddTrigger();
      this.searchTrigger = '';
      // Esconde banner de pendências — usuário precisa clicar Salvar novamente pra revê-las
      this.showSaveIssues = false;
    },
    'selectedObjectiveValidationIssues.length'(newLen) {
      // Esconde banner automaticamente quando todas as pendências forem resolvidas
      if (newLen === 0) this.showSaveIssues = false;
    },
    visible(val) {
      if (val && !this.configLoaded) {
        this.loadRtoConfig();
      }
    },
  },

  methods: {
    toggleSection(section) {
      this.openSection = this.openSection === section ? null : section;
    },

    // ── Gatilhos (nível objetivo) ──
    // Aplica `changes` ao gatilho aberto no modal, preservando reatividade.
    patchEditingGatilho(changes) {
      const objIdx = this.objectives.findIndex(o => o.id === this.editingObjectiveId);
      if (objIdx === -1) return;
      const obj = this.objectives[objIdx];
      const gatilhos = obj.gatilhos || [];
      const gatIdx = gatilhos.findIndex(g => g.id === this.editingGatilhoId);
      if (gatIdx === -1) return;
      const novos = gatilhos.slice();
      novos[gatIdx] = { ...novos[gatIdx], ...changes };
      this.$set(this.objectives, objIdx, { ...obj, gatilhos: novos });
    },

    addGatilho(objectiveId) {
      const objIdx = this.objectives.findIndex(o => o.id === objectiveId);
      if (objIdx === -1) return;
      const obj = this.objectives[objIdx];
      const novoGatilho = {
        id: safeRandomUUID(),
        rtoGatilhoId: null,
        nome: '',
        status: 'Inativo',
        elementos: [],
      };
      this.$set(this.objectives, objIdx, { ...obj, gatilhos: [...(obj.gatilhos || []), novoGatilho] });
      this.openGatilho(objectiveId, novoGatilho.id);
    },

    openGatilho(objectiveId, gatilhoId) {
      this.editingObjectiveId = objectiveId;
      this.editingGatilhoId = gatilhoId;
      this.cancelAddTrigger();
      this.searchTrigger = '';
      this.showTriggerModal = true;
    },

    toggleGatilhoStatus(objectiveId, gatilhoId) {
      const objIdx = this.objectives.findIndex(o => o.id === objectiveId);
      if (objIdx === -1) return;
      const obj = this.objectives[objIdx];
      const gatilhos = (obj.gatilhos || []).map(g =>
        g.id === gatilhoId ? { ...g, status: g.status === 'Ativo' ? 'Inativo' : 'Ativo' } : g
      );
      this.$set(this.objectives, objIdx, { ...obj, gatilhos });
    },

    removeGatilho(objectiveId, gatilhoId) {
      const objIdx = this.objectives.findIndex(o => o.id === objectiveId);
      if (objIdx === -1) return;
      const obj = this.objectives[objIdx];
      const gatilho = (obj.gatilhos || []).find(g => g.id === gatilhoId);
      // Já existia no RTO → agenda DELETE para o save().
      if (gatilho?.rtoGatilhoId && obj.rtoOtimizacaoId) {
        this.removedGatilhos.push({
          otimizacaoId: obj.rtoOtimizacaoId,
          rtoGatilhoId: gatilho.rtoGatilhoId,
        });
      }
      const gatilhos = (obj.gatilhos || []).filter(g => g.id !== gatilhoId);
      this.$set(this.objectives, objIdx, { ...obj, gatilhos });
      if (this.editingGatilhoId === gatilhoId) this.showTriggerModal = false;
    },

    confirmAddObjective() {
      if (!this.newObjectiveLabel.trim()) return;
      const newObj = {
        id: safeRandomUUID(),
        label: this.newObjectiveLabel.trim(),
        mode: 'optimize',
        optimizeType: 'max',
        fixedValue: 0,
        penaltyFactor: 1,
        corrente: null,
        correnteLabel: '',
        // Controles avançados do solver desligados por padrão num objetivo NOVO
        // (template usa opt-out `!== false`, então precisam vir explicitamente false).
        usarGrid: false,
        maxTempoEnable: false,
        usarPolish: false,
        gatilhos: [],
        // Mantemos uma única "área" interna (estrutura herdada) que contém
        // as variáveis manipuladas e restrições — sem UI de áreas no drawer.
        areas: [{ id: safeRandomUUID(), label: 'Padrão', variables: [], restrictions: [] }],
      };
      this.objectives.push(newObj);
      if (!this.selectedObjectiveId) this.selectedObjectiveId = newObj.id;
      this.newObjectiveLabel = '';
      this.isAddingObjective = false;
    },

    cancelAddObjective() {
      this.newObjectiveLabel = '';
      this.isAddingObjective = false;
    },

    updateObjective(updated) {
      const idx = this.objectives.findIndex(o => o.id === updated.id);
      if (idx !== -1) this.$set(this.objectives, idx, updated);
    },

    // Clique no radio de objetivo. Selecionar um objetivo = torná-lo a otimização ATIVA do RTO.
    // Abre modal de confirmação antes de ativar (a ativação desativa todas as outras no RTO).
    requestSelectObjective(obj) {
      if (!obj || obj.id === this.selectedObjectiveId) return; // já selecionado — no-op

      // Objetivo novo (ainda não salvo no RTO): não dá pra ativar o que não existe.
      // Só foca pra edição; ele nasce Ativo no RTO quando for salvo (POST /criar).
      if (!obj.rtoOtimizacaoId) {
        this.selectedObjectiveId = obj.id;
        return;
      }

      this.pendingActivateObj = obj;
      this.showActivateConfirm = true;
    },

    // Confirma a ativação: PUT /ativar no RTO (desativa as outras automaticamente) e reflete na UI.
    async confirmActivateObjective() {
      const obj = this.pendingActivateObj;
      if (!obj?.rtoOtimizacaoId) { this.showActivateConfirm = false; this.pendingActivateObj = null; return; }

      this.activating = true;
      try {
        await axios.put(`${urlMainAPI}/configurator/rto/ativar/${obj.rtoOtimizacaoId}`);
        // RTO garante só 1 ativa: marca o escolhido como Ativo e o resto Inativo (atualização otimista).
        this.objectives.forEach((o, i) =>
          this.$set(this.objectives, i, { ...o, status: o.id === obj.id ? 'Ativo' : 'Inativo' }));
        this.selectedObjectiveId = obj.id;
        defaultSuccess(`"${obj.label}" agora é a otimização ativa do RTO.`);
      } catch (e) {
        if (isRtoUnreachableError(e)) showError(RTO_UNREACHABLE_MESSAGE);
        else showError('Não foi possível ativar a otimização no RTO.');
      } finally {
        this.activating = false;
        this.showActivateConfirm = false;
        this.pendingActivateObj = null;
      }
    },

    async removeObjective(id) {
      const obj = this.objectives.find(o => o.id === id);
      if (!obj) return;

      // Se ainda não foi salvo no RTO, basta remover do estado local
      if (!obj.rtoOtimizacaoId) {
        this.removeObjectiveLocal(id);
        return;
      }

      try {
        await axios.delete(`${urlMainAPI}/configurator/rto/excluir/${obj.rtoOtimizacaoId}`);
        this.removeObjectiveLocal(id);
      } catch (err) {
        // 404: otimização já não existia no RTO — remove local mesmo assim
        if (err?.response?.status === 404) {
          this.removeObjectiveLocal(id);
          return;
        }
        if (isRtoUnreachableError(err)) {
          showError(RTO_UNREACHABLE_MESSAGE);
          return;
        }
        const msg = err?.response?.data?.description
          ?? err?.response?.data
          ?? err?.message
          ?? 'Erro ao excluir objetivo no RTO.';
        showError(typeof msg === 'string' ? msg : 'Erro ao excluir objetivo no RTO.');
      }
    },

    removeObjectiveLocal(id) {
      this.objectives = this.objectives.filter(o => o.id !== id);
      if (this.selectedObjectiveId === id) {
        // Volta para a otimização ATIVA do RTO (status Ativo), não o primeiro da lista.
        this.selectedObjectiveId = this.objectives.find(o => o.status === 'Ativo')?.id
          ?? this.objectives[0]?.id ?? null;
      }
    },

    // ── Critérios de Estabilidade ──
    setStabilityCorrente(e) {
      this.newStabilityCriterion.corrente = e.target.value || null;
      this.newStabilityCriterion.propriedade = null;
      this.newStabilityCriterion.atributo = null;
    },
    setStabilityPropriedade(e) {
      const name = e.target.value;
      this.newStabilityCriterion.propriedade = name
        ? (this.stabilityPropriedadeList.find(p => p.name === name) || null)
        : null;
      this.newStabilityCriterion.atributo = null;
    },
    setStabilityAtributo(e) {
      this.newStabilityCriterion.atributo = e.target.value || null;
    },
    editStabilityCriterion(index) {
      if (this.editingStabilityIndex === index) return;
      this.editingStabilityIndex = index;
      this.newStabilityCriterion = { ...this.stabilityCriteria[index] };
      this.isAddingStabilityCriterion = true;
    },
    confirmAddStabilityCriterion() {
      this.stabilitySubmitAttempted = true;
      const c = this.newStabilityCriterion;
      if (!c.corrente || !c.propriedade || !c.atributo || !c.numAtualizacoes || c.variacao === null) return;
      if (this.editingStabilityIndex !== null) {
        this.$set(this.stabilityCriteria, this.editingStabilityIndex, { ...this.newStabilityCriterion });
      } else {
        this.stabilityCriteria.push({ ...this.newStabilityCriterion });
      }
      this.newStabilityCriterion = { corrente: null, propriedade: null, atributo: null, numAtualizacoes: null, variacao: null, min: 0, max: 1000000 };
      this.editingStabilityIndex = null;
      this.isAddingStabilityCriterion = false;
      this.stabilitySubmitAttempted = false;
    },
    cancelAddStabilityCriterion() {
      this.newStabilityCriterion = { corrente: null, propriedade: null, atributo: null, numAtualizacoes: null, variacao: null, min: 0, max: 1000000 };
      this.editingStabilityIndex = null;
      this.isAddingStabilityCriterion = false;
      this.stabilitySubmitAttempted = false;
    },
    removeStabilityCriterion(index) {
      this.stabilityCriteria.splice(index, 1);
      if (this.stabilityCriteria.length <= 4) this.searchStability = '';
      if (this.editingStabilityIndex === index) {
        this.cancelAddStabilityCriterion();
      } else if (this.editingStabilityIndex !== null && this.editingStabilityIndex > index) {
        this.editingStabilityIndex--;
      }
    },

    // ── Gatilhos para atuação do RTO ──
    setTriggerCorrente(e) {
      this.newTrigger.corrente = e.target.value || null;
      this.newTrigger.propriedade = null;
      this.newTrigger.atributo = null;
    },
    setTriggerPropriedade(e) {
      const name = e.target.value;
      this.newTrigger.propriedade = name
        ? (this.triggerPropriedadeList.find(p => p.name === name) || null)
        : null;
      this.newTrigger.atributo = null;
    },
    setTriggerAtributo(e) {
      this.newTrigger.atributo = e.target.value || null;
    },
    // Grava os elementos no gatilho aberto no modal.
    setSelectedTriggers(newTriggers) {
      this.patchEditingGatilho({ elementos: newTriggers });
    },
    editTrigger(index) {
      if (this.editingTriggerIndex === index) return;
      this.editingTriggerIndex = index;
      this.newTrigger = { ...this.selectedTriggers[index] };
      this.isAddingTrigger = true;
    },
    confirmAddTrigger() {
      this.triggerSubmitAttempted = true;
      const t = this.newTrigger;
      if (!t.corrente || !t.propriedade || !t.atributo || !t.numAtualizacoes || t.numAtualizacoes < 2 || t.variacao === null) return;
      const updated = [...this.selectedTriggers];
      if (this.editingTriggerIndex !== null) {
        updated[this.editingTriggerIndex] = { ...this.newTrigger };
      } else {
        updated.push({ ...this.newTrigger });
      }
      this.setSelectedTriggers(updated);
      this.newTrigger = { corrente: null, propriedade: null, atributo: null, numAtualizacoes: null, variacao: null, min: 0, max: 1000000, valoresEspera: 3, pausado: false };
      this.editingTriggerIndex = null;
      this.isAddingTrigger = false;
      this.triggerSubmitAttempted = false;
    },
    cancelAddTrigger() {
      this.newTrigger = { corrente: null, propriedade: null, atributo: null, numAtualizacoes: null, variacao: null, min: 0, max: 1000000, valoresEspera: 3, pausado: false };
      this.editingTriggerIndex = null;
      this.isAddingTrigger = false;
      this.triggerSubmitAttempted = false;
    },
    removeTrigger(index) {
      const updated = [...this.selectedTriggers];
      updated.splice(index, 1);
      this.setSelectedTriggers(updated);
      if (updated.length <= 4) this.searchTrigger = '';
      if (this.editingTriggerIndex === index) {
        this.cancelAddTrigger();
      } else if (this.editingTriggerIndex !== null && this.editingTriggerIndex > index) {
        this.editingTriggerIndex--;
      }
    },

    deleteAll(target) {
      if (target === 'stability') {
        this.stabilityCriteria = [];
        this.searchStability = '';
        this.cancelAddStabilityCriterion();
      } else {
        this.setSelectedTriggers([]);
        this.searchTrigger = '';
        this.cancelAddTrigger();
      }
    },

    importJson() {
      this.$refs.fileInput.click();
    },

    onFileSelected(event) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const config = JSON.parse(e.target.result);
          this.maxIterations = config.maxIterations ?? 100;
          this.tolSimulacao = config.tolSimulacao ?? 0.0001;
          this.tolOtimizacao = config.tolOtimizacao ?? 0.1;
          this.maxIteracoesPorLM = config.maxIteracoesPorLM ?? 35;
          // Normaliza gatilhos: JSON antigo guardava triggerName/triggers por objetivo —
          // vira um único gatilho na lista nova obj.gatilhos[].
          this.objectives = (Array.isArray(config.objectives) ? config.objectives : []).map(o => {
            if (Array.isArray(o.gatilhos)) return o;
            const elementos = Array.isArray(o.triggers) ? o.triggers : [];
            const gatilhos = (elementos.length > 0 || o.triggerName)
              ? [{ id: safeRandomUUID(), rtoGatilhoId: null, nome: o.triggerName || '', status: 'Inativo', elementos }]
              : [];
            const rest = { ...o };
            delete rest.triggerName;
            delete rest.triggers;
            return { ...rest, gatilhos };
          });
          // Retrocompatibilidade: JSON antigo guardava usarGrid/tamanhoGrid/multiStartK/maxTempo*/usarPolish globais — agora ficam por objetivo
          if (config.usarGrid != null || config.tamanhoGrid != null || config.multiStartK != null ||
              config.maxTempoEnable != null || config.maxTempoSegundos != null || config.usarPolish != null) {
            this.objectives = this.objectives.map(o => ({
              ...o,
              usarGrid: o.usarGrid ?? config.usarGrid ?? true,
              tamanhoGrid: o.tamanhoGrid ?? config.tamanhoGrid ?? config.divisoes ?? 10,
              multiStartK: o.multiStartK ?? config.multiStartK ?? config.kernels ?? 3,
              maxTempoEnable: o.maxTempoEnable ?? config.maxTempoEnable ?? true,
              maxTempoSegundos: o.maxTempoSegundos ?? config.maxTempoSegundos ?? 300,
              usarPolish: o.usarPolish ?? config.usarPolish ?? true,
            }));
          }
          this.selectedObjectiveId = config.selectedObjectiveId ?? this.objectives[0]?.id ?? null;
          this.stabilityName = config.stabilityName ?? 'Estabilidade Global';
          this.stabilityCriteria = Array.isArray(config.stabilityCriteria) ? config.stabilityCriteria : [];
        } catch {
          showError('Arquivo JSON inválido. Verifique o formato e tente novamente.');
        }
      };
      reader.readAsText(file);
      event.target.value = '';
    },

    exportJson() {
      const config = {
        maxIterations: this.maxIterations,
        tolSimulacao: this.tolSimulacao,
        tolOtimizacao: this.tolOtimizacao,
        maxIteracoesPorLM: this.maxIteracoesPorLM,
        selectedObjectiveId: this.selectedObjectiveId,
        objectives: this.objectives,
        stabilityName: this.stabilityName,
        stabilityCriteria: this.stabilityCriteria,
      };
      const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(config, null, 2));
      const anchor = document.createElement('a');
      anchor.href = dataStr;
      anchor.download = 'configuracao_rto.json';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    },

    parseTag(tag) {
      if (!tag || typeof tag !== 'string') return null;
      const parts = tag.split('|');
      const type = parts[0];
      // cr|<corrente>|Atributos|<attr>           — atributo direto da corrente
      // cp|<corrente>|<componente>|<attr>        — atributo de componente
      if ((type === 'cr' || type === 'cp') && parts.length === 4) {
        return { origin: 'Corrente', corrente: parts[1], propriedade: { name: parts[2] }, atributo: parts[3] };
      }
      // op|<TipoOp>|<NomeOp>|Atributos|<attr>    — atributo de operação (formato canônico RTO)
      if (type === 'op' && parts.length === 5) {
        const opIndex = `${parts[1]}|${parts[2]}`;
        return { origin: 'Operação', operation: opIndex, operationAtributo: parts[4], atributo: parts[4] };
      }
      // op|<TipoOp>|<key>                        — variante curta (engineeringMapping.setMapping)
      if (type === 'op' && parts.length === 3) {
        return { origin: 'Operação', operation: parts[1], operationAtributo: parts[2], atributo: parts[2] };
      }
      // in|<corrente>|<componente>|Atributos|<attr>  — corrente interna, atributo direto
      if (type === 'in' && parts.length === 5) {
        return { origin: 'Corrente', corrente: parts[1], propriedade: { name: parts[2] }, atributo: parts[4] };
      }
      // ci|<corrente>|<componente>|<subType>|<attr>  — corrente interna com subtipo
      if (type === 'ci' && parts.length === 5) {
        return { origin: 'Corrente', corrente: parts[1], propriedade: { name: parts[2] }, atributo: parts[4] };
      }
      // eq|<corrente>|<equip>|<attr>             — equipamento direto
      if (type === 'eq' && parts.length === 4) {
        return { origin: 'Corrente', corrente: parts[1], propriedade: { name: parts[2] }, atributo: parts[3] };
      }
      // eq|<corrente>|<prop>|<equip>|<attr>      — equipamento com prop
      if (type === 'eq' && parts.length === 5) {
        return { origin: 'Corrente', corrente: parts[1], propriedade: { name: parts[3] }, atributo: parts[4] };
      }
      return null;
    },

    buildObjectiveFromRto(otm) {
      const elements = Array.isArray(otm.elementos) ? otm.elementos : [];
      const objElem = elements.find(e => e.tipo === 'Objetivo' || e.tipo === 0);
      const parsedObj = objElem ? this.parseTag(objElem.tag) : null;

      const mode = (objElem?.objetivoTipo === 'Fixed' || objElem?.objetivoTipo === 2) ? 'fixed' : 'optimize';
      const optimizeType = (objElem?.objetivoTipo === 'Min' || objElem?.objetivoTipo === 0) ? 'min' : 'max';

      const variables = [];
      const restrictions = [];
      for (const el of elements) {
        if (el.tipo === 'Objetivo' || el.tipo === 0) continue;
        const parsed = this.parseTag(el.tag);
        const isOp = parsed?.origin === 'Operação';
        const sourceLabel = isOp ? (parsed?.operation || '').replace('|', ' / ') : parsed?.corrente;
        // Conversão RTO→PAP (fração/Kelvin → %/Celsius) via fromRtoScale.
        // Defaults 0/100 são em escala PAP, aplicados quando o banco não traz min/max.
        const base = {
          id: safeRandomUUID(),
          origin: parsed?.origin ?? 'Corrente',
          corrente: isOp ? null : (parsed?.corrente ?? null),
          propriedade: isOp ? null : (parsed?.propriedade ?? null),
          atributo: parsed?.atributo ?? null,
          operation: isOp ? (parsed?.operation ?? null) : null,
          operationAtributo: isOp ? (parsed?.operationAtributo ?? null) : null,
          label: el.label || sourceLabel || el.tag,
          min: el.min != null ? fromRtoScale(el.min, el.tag) : 0,
          max: el.max != null ? fromRtoScale(el.max, el.tag) : 100,
          mapping: el.tag,
        };
        if (el.tipo === 'Manipulada' || el.tipo === 1) {
          variables.push(base);
        } else if (el.tipo === 'Restricao' || el.tipo === 2) {
          restrictions.push({
            ...base,
            enabled: true,
            penaltyFactor: el.penaltyFactor ?? 7000,
            tipoPenalidade: el.tipoPenalidade ?? 'Quadratica',
          });
        }
      }

      const isObjOp = parsedObj?.origin === 'Operação';
      return {
        id: String(otm.id),
        label: otm.nome,
        rtoOtimizacaoId: String(otm.id),
        // Status da otimizacao no RTO ("Ativo"/"Inativo"). Usado pra pre-selecionar o objetivo
        // ativo no Drawer e refletir a troca apos PUT /ativar.
        status: otm.status ?? 'Inativo',
        mode,
        optimizeType,
        // Conversão RTO→PAP via fromRtoScale (cobre vmC, outros PropertyBrix, Temperature, Terra em 1_1)
        fixedValue: objElem?.fixedValue != null ? fromRtoScale(objElem.fixedValue, objElem.tag) : 0,
        penaltyFactor: objElem?.penaltyFactor ?? 1,
        min: objElem?.min != null ? fromRtoScale(objElem.min, objElem.tag) : null,
        max: objElem?.max != null ? fromRtoScale(objElem.max, objElem.tag) : null,
        usarGrid: otm.usarGrid ?? true,
        tamanhoGrid: otm.tamanhoGrid ?? 10,
        multiStartK: otm.multiStartK ?? 3,
        maxTempoEnable: otm.maxTempoEnable ?? true,
        maxTempoSegundos: otm.maxTempoSegundos ?? 300,
        usarPolish: otm.usarPolish ?? true,
        origin: parsedObj?.origin ?? 'Corrente',
        corrente: isObjOp ? null : (parsedObj?.corrente ?? null),
        correnteLabel: '',
        propriedade: isObjOp ? null : (parsedObj?.propriedade ?? null),
        atributo: parsedObj?.atributo ?? null,
        operation: isObjOp ? (parsedObj?.operation ?? null) : null,
        operationAtributo: isObjOp ? (parsedObj?.operationAtributo ?? null) : null,
        gatilhos: [],
        areas: [{
          id: safeRandomUUID(),
          label: 'Padrão',
          variables,
          restrictions,
        }],
      };
    },

    parseStatusOtimizador(raw) {
      if (typeof raw === 'number') return raw === 2 ? 2 : 1;
      if (typeof raw === 'string') return raw.toLowerCase() === 'inativo' ? 2 : 1;
      return 1;
    },

    async loadConfigGlobal() {
      if (!this.hasGlobalConfigPermission) return;
      this.configGlobalLoading = true;
      this.configGlobalError = null;
      try {
        const res = await axios.get(`${urlMainAPI}/configurator/rto/configuracao`);
        const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        const cfg = data?.data ?? data ?? {};
        this.configGlobal = {
          simulatorIp: cfg.simulatorIp ?? '',
          simulatorIp2: cfg.simulatorIp2 ?? '',
          simulatorPort: cfg.simulatorPort ?? null,
          statusOtimizador: this.parseStatusOtimizador(cfg.statusOtimizador),
        };
        // Mantém o indicador "RTO ATIVO" do Header sincronizado (1 = Ativo)
        this.$store.commit('setRtoActive', this.configGlobal.statusOtimizador === 1);
      } catch (e) {
        console.warn('Erro ao carregar ConfiguracaoRTO:', e?.response?.status);
        this.configGlobalError = e?.message ?? 'Erro ao carregar.';
      } finally {
        this.configGlobalLoading = false;
      }
    },

    async saveConfigGlobal() {
      if (!this.hasGlobalConfigPermission) return;
      try {
        await axios.put(`${urlMainAPI}/configurator/rto/configuracao`, {
          simulatorIp: this.configGlobal.simulatorIp || null,
          simulatorIp2: this.configGlobal.simulatorIp2 || null,
          simulatorPort: this.configGlobal.simulatorPort,
        });
        await axios.put(
          `${urlMainAPI}/configurator/rto/configuracao/status?statusOtimizador=${this.configGlobal.statusOtimizador}`
        );
        // Mantém o indicador "RTO ATIVO" do Header sincronizado (1 = Ativo)
        this.$store.commit('setRtoActive', this.configGlobal.statusOtimizador === 1);
      } catch (e) {
        console.error('Erro ao salvar ConfiguracaoRTO:', e);
        throw e;
      }
    },

    // CA-08.3: load/save dos overrides "incluir/excluir indicador por objetivo".
    async loadAllIndicators() {
      try {
        const res = await axios.get(`${urlMainAPI}/getindicatorlist`);
        const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        const arr = Array.isArray(data) ? data : [];
        // Dedup por (indicatorId, productionUnitId, system) — o endpoint retorna o mesmo
        // indicador várias vezes (JOIN com indicatorTree pode ter N árvores referenciando
        // o mesmo Indicator), o que geraria chaves duplicadas no v-for.
        const seen = new Set();
        this.allIndicators = arr.filter(i => {
          const k = `${i.indicatorId}-${i.productionUnitId}-${i.system}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
      } catch (e) {
        console.warn('Erro ao carregar lista de indicadores:', e?.response?.status);
        this.allIndicators = [];
      }
    },

    async loadRtoConfigOverrides() {
      try {
        const res = await axios.get(`${urlMainAPI}/configurator/getrtoconfig`);
        let data = res.data;
        if (typeof data === 'string') data = data ? JSON.parse(data) : null;
        const overrides = data && typeof data === 'object' ? data.rtoIndicatorOverrides : null;
        this.rtoIndicatorOverrides = overrides && typeof overrides === 'object' ? overrides : {};
      } catch (e) {
        console.warn('Erro ao carregar overrides RTO:', e?.response?.status);
        this.rtoIndicatorOverrides = {};
      }
    },

    async saveRtoConfigOverrides() {
      try {
        await axios.post(`${urlMainAPI}/configurator/savertoconfig`, {
          rtoIndicatorOverrides: this.rtoIndicatorOverrides,
        });
      } catch (e) {
        console.warn('Erro ao salvar overrides RTO:', e?.response?.status);
      }
    },

    onPhase3SearchInput(e) {
      // Atualiza imediatamente o valor visível (input controlado) — sem isso, digitar
      // rápido perde teclas porque o re-render do filteredPhase3Indicators (200+ items)
      // intercepta o handler do v-model.
      this.phase3SearchInput = e.target.value;
      if (this.phase3SearchDebounce) clearTimeout(this.phase3SearchDebounce);
      this.phase3SearchDebounce = setTimeout(() => {
        this.phase3SearchTerm = this.phase3SearchInput;
        this.phase3SearchDebounce = null;
      }, 180);
    },

    getOverride(otmId, indicatorKey) {
      if (!otmId || !indicatorKey) return 'auto';
      const perObj = this.rtoIndicatorOverrides[String(otmId)];
      if (!perObj) return 'auto';
      const mode = perObj[indicatorKey];
      return mode === 'include' || mode === 'exclude' ? mode : 'auto';
    },

    setOverride(otmId, indicatorKey, mode) {
      if (!otmId || !indicatorKey) return;
      const key = String(otmId);
      const perObj = { ...(this.rtoIndicatorOverrides[key] || {}) };
      if (mode === 'auto') {
        delete perObj[indicatorKey];
      } else {
        perObj[indicatorKey] = mode;
      }
      const next = { ...this.rtoIndicatorOverrides };
      if (Object.keys(perObj).length === 0) delete next[key];
      else next[key] = perObj;
      this.rtoIndicatorOverrides = next;
    },

    parseMonitoringElement(el) {
      const parsed = this.parseTag(el.tag);
      return {
        corrente: parsed?.corrente ?? null,
        propriedade: parsed?.propriedade ?? null,
        atributo: parsed?.atributo ?? null,
        numAtualizacoes: el.ultimosValores ?? null,
        variacao: el.percentual ?? null,
        min: el.min ?? 0,
        max: el.max ?? 1000000,
        valoresEspera: el.valoresEspera ?? 3,
        pausado: el.pausado ?? false,
      };
    },

    mapMonitoringElement(item, extras = {}) {
      const tag = EngineeringMapping.setMapping({
        type: 'Correntes',
        index: item.corrente,
        propName: item.propriedade?.name,
        key: item.atributo,
      });
      return {
        tag,
        min: item.min ?? 0,
        max: item.max ?? 1e9,
        percentual: item.variacao ?? 0,
        ultimosValores: item.numAtualizacoes ?? 10,
        ...extras,
      };
    },

    async loadRtoConfig() {
      try {
        const res = await axios.get(`${urlMainAPI}/configurator/rto/listar`);
        const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        const list = (Array.isArray(data?.data) ? data.data : [])
          .filter(o => o.status !== 'Deletado');

        this.objectives = list.map(otm => this.buildObjectiveFromRto(otm));

        if (this.objectives.length > 0) {
          // Pre-seleciona o objetivo ATIVO no RTO (so 1 ativa por vez); fallback no primeiro da lista.
          this.selectedObjectiveId = this.objectives.find(o => o.status === 'Ativo')?.id ?? this.objectives[0].id;
          const first = list[0];
          if (first.maxIteracoes != null) this.maxIterations = first.maxIteracoes;
          if (first.toleranciaSimulacao != null) this.tolSimulacao = first.toleranciaSimulacao;
          if (first.toleranciaOtimizacao != null) this.tolOtimizacao = first.toleranciaOtimizacao;
          if (first.maxIteracoesPorLM != null) this.maxIteracoesPorLM = first.maxIteracoesPorLM;

          // Carrega TODOS os gatilhos de cada objetivo do RTO em paralelo
          await Promise.all(this.objectives.map(async (obj) => {
            if (!obj.rtoOtimizacaoId) return;
            try {
              const gRes = await axios.get(`${urlMainAPI}/configurator/rto/gatilhos/${obj.rtoOtimizacaoId}`);
              const gData = typeof gRes.data === 'string' ? JSON.parse(gRes.data) : gRes.data;
              const idx = this.objectives.findIndex(o => o.id === obj.id);
              if (idx === -1) return;
              const lista = Array.isArray(gData?.data) ? gData.data : [];
              this.$set(this.objectives, idx, {
                ...this.objectives[idx],
                gatilhos: lista.map(g => ({
                  id: safeRandomUUID(),
                  rtoGatilhoId: String(g.id),
                  nome: g.nome ?? '',
                  status: g.status === 'Ativo' ? 'Ativo' : 'Inativo',
                  elementos: (g.elementos ?? []).map(el => this.parseMonitoringElement(el)),
                })),
              });
            } catch (e) {
              console.warn(`Sem gatilhos carregados para Otimização ${obj.rtoOtimizacaoId}:`, e?.response?.status);
            }
          }));
        }

        try {
          const eRes = await axios.get(`${urlMainAPI}/configurator/rto/estabilidade`);
          const eData = typeof eRes.data === 'string' ? JSON.parse(eRes.data) : eRes.data;
          // Resposta atual: array direto. Quando RTO expor nome, ler de eData.data.nome
          if (eData?.data?.nome) this.stabilityName = eData.data.nome;
          const elementos = Array.isArray(eData?.data) ? eData.data : (Array.isArray(eData?.data?.elementos) ? eData.data.elementos : []);
          this.stabilityCriteria = elementos.map(el => this.parseMonitoringElement(el));
        } catch (e) {
          console.warn('Sem estabilidade carregada:', e?.response?.status);
          this.stabilityCriteria = [];
        }

        await this.loadConfigGlobal();

        // CA-08.3: lista de indicadores + overrides (só para Pentagro/guardião).
        if (this.hasGlobalConfigPermission) {
          await Promise.all([this.loadAllIndicators(), this.loadRtoConfigOverrides()]);
        }

        this.configLoaded = true;
      } catch (e) {
        console.error('Erro ao carregar otimizações do RTO:', e);
        this.configLoaded = true;
      }
    },

    buildHistorianEntries() {
      const selected = this.objectives.find(o => o.id === this.selectedObjectiveId);
      if (!selected) return [];
      const entries = [];
      for (const area of (selected.areas || [])) {
        for (const variable of (area.variables || [])) {
          if (variable.mapping) {
            const valor = SingletonEnginnering.getValueByToken(variable.mapping);
            entries.push({ tag: variable.mapping, valor: valor ?? 0 });
          }
        }
      }
      return entries;
    },

    buildObjectiveTag(obj) {
      if (obj.origin === 'Operação') {
        if (!obj.operation || !obj.operationAtributo) return '';
        return `op|${obj.operation}|Atributos|${obj.operationAtributo}`;
      }
      if (!obj.corrente || !obj.propriedade || !obj.atributo) return '';
      return EngineeringMapping.setMapping({
        type: 'Correntes',
        index: obj.corrente,
        propName: obj.propriedade.name,
        key: obj.atributo,
      });
    },

    // Retorna lista de TODAS as pendências do objetivo (não para na primeira).
    // Usada tanto pelo banner visual (proativo) quanto pelo save() (defensivo).
    getObjectiveValidationIssues(obj) {
      const issues = [];
      if (!obj) {
        issues.push('Selecione um objetivo no card "Operacional" antes de salvar.');
        return issues;
      }

      if (!obj.label || !obj.label.trim()) {
        issues.push('O objetivo precisa de um nome.');
      }

      // Variável objetivo (Corrente ou Operação)
      const objectiveTag = this.buildObjectiveTag(obj);
      if (!objectiveTag) {
        if (obj.origin === 'Operação') {
          issues.push('Variável objetivo: selecione a Operação e o Atributo.');
        } else {
          issues.push('Variável objetivo: selecione Corrente, Propriedade e Atributo.');
        }
      }

      // Min/Max do objetivo (não exigidos em modo Fixed)
      if (obj.mode !== 'fixed') {
        if (obj.min == null || obj.min === '') {
          issues.push('Variável objetivo: preencha o Mínimo (alvo do solver no modo Minimizar / limite inferior aceitável).');
        }
        if (obj.max == null || obj.max === '') {
          issues.push('Variável objetivo: preencha o Máximo (alvo do solver no modo Maximizar / limite superior aceitável).');
        }
        if (obj.min != null && obj.max != null && Number(obj.min) >= Number(obj.max)) {
          issues.push(`Variável objetivo: Mínimo (${obj.min}) deve ser menor que Máximo (${obj.max}).`);
        }
      } else if (obj.fixedValue == null || obj.fixedValue === '') {
        issues.push('Variável objetivo: preencha o Valor Fixo (alvo do solver).');
      }

      const variables = [];
      const restrictions = [];
      for (const area of (obj.areas || [])) {
        for (const v of (area.variables || [])) variables.push(v);
        for (const r of (area.restrictions || [])) {
          if (r.enabled !== false) restrictions.push(r);
        }
      }

      // Pelo menos 1 manipulada (regra do RTO: OtimizacaoController.Criar exige Tipo=Manipulada)
      if (variables.length === 0) {
        issues.push('Adicione pelo menos uma variável manipulada na seção "Variáveis".');
      } else {
        for (const v of variables) {
          const nome = v.label || v.corrente || (v.operation || '').replace('|', ' / ') || 'sem nome';
          if (!v.mapping) {
            issues.push(`Variável manipulada "${nome}": selecione a Origem e o Atributo.`);
            continue;
          }
          if (v.min == null || v.max == null || v.min === '' || v.max === '') {
            issues.push(`Variável manipulada "${nome}": preencha Mínimo e Máximo.`);
            continue;
          }
          if (Number(v.min) >= Number(v.max)) {
            issues.push(`Variável manipulada "${nome}": Mínimo (${v.min}) deve ser menor que Máximo (${v.max}).`);
          }
        }
      }

      // Restrições (ativas) precisam estar completas
      for (const r of restrictions) {
        const nome = r.label || r.corrente || (r.operation || '').replace('|', ' / ') || 'sem nome';
        if (!r.mapping) {
          issues.push(`Restrição "${nome}": selecione a Origem e o Atributo (ou desmarque o checkbox para ignorá-la).`);
          continue;
        }
        if (r.min == null || r.max == null || r.min === '' || r.max === '') {
          issues.push(`Restrição "${nome}": preencha Mínimo e Máximo.`);
          continue;
        }
        if (Number(r.min) >= Number(r.max)) {
          issues.push(`Restrição "${nome}": Mínimo (${r.min}) deve ser menor que Máximo (${r.max}).`);
        }
      }

      // Tags duplicadas (RTO rejeita: "Tags duplicadas")
      if (objectiveTag) {
        const sourcesByTag = {};
        const register = (tag, source) => {
          if (!tag) return;
          sourcesByTag[tag] = sourcesByTag[tag] || [];
          sourcesByTag[tag].push(source);
        };
        register(objectiveTag, 'Variável objetivo');
        variables.forEach(v => {
          const nome = v.label || v.corrente || (v.operation || '').replace('|', ' / ') || 'sem nome';
          register(v.mapping, `Variável "${nome}"`);
        });
        restrictions.forEach(r => {
          const nome = r.label || r.corrente || (r.operation || '').replace('|', ' / ') || 'sem nome';
          register(r.mapping, `Restrição "${nome}"`);
        });
        for (const [tag, sources] of Object.entries(sourcesByTag)) {
          if (sources.length > 1) {
            issues.push(`Tag duplicada (${tag}): ${sources.join(' + ')} — cada elemento precisa de uma tag única.`);
          }
        }
      }

      // Configs do solver por objetivo
      if (obj.usarGrid !== false) {
        const tg = obj.tamanhoGrid;
        if (tg != null && (!tg || tg < 1 || !Number.isInteger(tg))) {
          issues.push('Tamanho do Grid: deve ser inteiro maior que 0.');
        }
        const k = obj.multiStartK;
        if (k != null && (!k || k < 1 || !Number.isInteger(k))) {
          issues.push('Multi-start K: deve ser inteiro maior que 0.');
        }
      }
      if (obj.maxTempoEnable !== false) {
        const t = obj.maxTempoSegundos;
        if (t != null && (!t || t < 1 || !Number.isInteger(t))) {
          issues.push('Tempo máximo (s): deve ser inteiro maior que 0.');
        }
      }

      // Gatilho obrigatório — sem um gatilho com ao menos 1 elemento válido o RTO
      // aceita a criação mas a otimização fica "fantasma" (não aparece no Assistente).
      const gatilhos = Array.isArray(obj.gatilhos) ? obj.gatilhos : [];
      const isElementoValido = e => e && e.corrente && e.propriedade && e.atributo;
      const gatilhoValido = gatilhos.find(g => (g.elementos ?? []).some(isElementoValido));
      if (!gatilhoValido) {
        if (gatilhos.length === 0) {
          issues.push('Adicione pelo menos um gatilho na seção "Gatilhos para atuação do RTO".');
        } else {
          issues.push('Configure pelo menos um elemento (Corrente + Propriedade + Atributo) em um dos gatilhos.');
        }
      }

      return issues;
    },

    buildElementos(obj, objectiveTag) {
      const objetivoTipo = obj.mode === 'fixed' ? 2 : (obj.optimizeType === 'min' ? 0 : 1);
      const objetivoElem = {
        tipo: 0,
        tag: objectiveTag,
        label: obj.label || null,
        objetivoTipo,
        penaltyFactor: obj.penaltyFactor ?? 1,
      };
      // Min/Max do objetivo são usados pelo solver como alvo no cálculo do penalty:
      // ObjetivoTipo=Min → penalty = pf*(sim-min)²; Max → penalty = pf*(max-sim)². Sem eles o solver
      // fica sem direção numérica. Só Fixed dispensa (usa fixedValue como alvo).
      // Conversão PAP→RTO (%/Celsius → fração/Kelvin) via toRtoScale.
      if (obj.mode === 'fixed') {
        objetivoElem.fixedValue = toRtoScale(obj.fixedValue, objectiveTag);
      } else {
        if (obj.min != null) objetivoElem.min = toRtoScale(obj.min, objectiveTag);
        if (obj.max != null) objetivoElem.max = toRtoScale(obj.max, objectiveTag);
      }
      const elementos = [objetivoElem];
      const usedTags = new Set([objectiveTag]);
      for (const area of (obj.areas || [])) {
        for (const v of (area.variables || [])) {
          if (v.mapping && !usedTags.has(v.mapping)) {
            usedTags.add(v.mapping);
            elementos.push({
              tipo: 1,
              tag: v.mapping,
              label: v.label || v.corrente || (v.operation || '').replace('|', ' / ') || null,
              min: toRtoScale(v.min, v.mapping),
              max: toRtoScale(v.max, v.mapping),
            });
          }
        }
        for (const r of (area.restrictions || [])) {
          if (r.mapping && r.enabled !== false && !usedTags.has(r.mapping)) {
            usedTags.add(r.mapping);
            elementos.push({
              tipo: 2,
              tag: r.mapping,
              label: r.label || r.corrente || (r.operation || '').replace('|', ' / ') || null,
              min: toRtoScale(r.min, r.mapping),
              max: toRtoScale(r.max, r.mapping),
              penaltyFactor: r.penaltyFactor ?? 7000,
              tipoPenalidade: r.tipoPenalidade ?? 'Quadratica',
            });
          }
        }
      }
      return elementos;
    },

    // Persiste UM objetivo no RTO (POST se novo, PUT se já existe) e sincroniza
    // os gatilhos desse objetivo. A validação e a fase global (estabilidade,
    // config global, overrides) ficam no save() — este método cuida só do `obj` recebido.
    // Lança em caso de erro para o save() registrar a falha por objetivo.
    async saveSingleObjective(obj) {
      const objectiveTag = this.buildObjectiveTag(obj);
      const elementos = this.buildElementos(obj, objectiveTag);

      const usarGrid = obj.usarGrid !== false;
      // maxIteracoes/tolerâncias/maxIteracoesPorLM são globais no UI (uma única caixa) mas o
      // backend RTO persiste por-otimização — enviamos o mesmo valor em cada objetivo para
      // manter todas as otimizações sincronizadas (o loadRtoConfig lê esses campos do 1º objetivo).
      const payload = {
        nome: obj.label,
        maxIteracoes: this.maxIterations,
        toleranciaSimulacao: this.tolSimulacao,
        toleranciaOtimizacao: this.tolOtimizacao,
        usarGrid,
        tamanhoGrid: obj.tamanhoGrid ?? 10,
        multiStartK: usarGrid ? (obj.multiStartK ?? 3) : 1,
        maxIteracoesPorLM: this.maxIteracoesPorLM,
        maxTempoEnable: obj.maxTempoEnable !== false,
        maxTempoSegundos: obj.maxTempoSegundos ?? 300,
        usarPolish: obj.usarPolish !== false,
        elementos,
      };

      // Monta os elementos de um gatilho no formato do RTO (descarta elementos incompletos).
      const buildElementosGatilho = (g) => (g.elementos ?? [])
        .filter(t => t.corrente && t.propriedade && t.atributo)
        .map(t => this.mapMonitoringElement(t, {
          valoresEspera: t.valoresEspera ?? 3,
          pausado: t.pausado ?? false,
        }));

      let savedId = null;
      if (obj.rtoOtimizacaoId) {
        try {
          await axios.put(`${urlMainAPI}/configurator/rto/atualizar/${obj.rtoOtimizacaoId}`, payload);
          savedId = obj.rtoOtimizacaoId;
        } catch (err) {
          if (err?.response?.status !== 404) throw err;
        }
      }
      if (!savedId) {
        // O RTO exige um gatilho na criação da otimização — a validação já garantiu
        // que existe ao menos um gatilho com elementos válidos. Escolhe o primeiro
        // que tenha elementos válidos (não necessariamente o gatilhos[0]).
        const primeiro = (obj.gatilhos ?? []).find(g => buildElementosGatilho(g).length > 0)
          || (obj.gatilhos ?? [])[0];
        payload.gatilho = {
          nome: primeiro.nome || `GTL-${obj.label}`,
          elementos: buildElementosGatilho(primeiro),
        };
        const res = await axios.post(`${urlMainAPI}/configurator/rto/criar`, payload);
        const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        const newId = data?.data?.id ?? null;
        if (!newId) throw new Error(`ID não retornado pelo RTO. Resposta: ${JSON.stringify(data).slice(0, 200)}`);
        savedId = String(newId);
        const novoGatilhoId = data?.data?.gatilhoId != null ? String(data.data.gatilhoId) : null;
        const idx = this.objectives.findIndex(o => o.id === obj.id);
        if (idx !== -1) {
          const o2 = this.objectives[idx];
          const gats = (o2.gatilhos || []).slice();
          // O gatilho enviado em payload.gatilho recebe o id retornado pelo RTO.
          const primeiroIdx = gats.findIndex(g => g.id === primeiro.id);
          if (primeiroIdx !== -1 && novoGatilhoId) {
            gats[primeiroIdx] = { ...gats[primeiroIdx], rtoGatilhoId: novoGatilhoId };
          }
          this.$set(this.objectives, idx, { ...o2, rtoOtimizacaoId: savedId, gatilhos: gats });
        }
      }

      // Sincroniza gatilhos: cria os novos, atualiza os existentes, ajusta o status.
      const objAtual = this.objectives.find(o => o.id === obj.id);
      for (const gatilho of (objAtual?.gatilhos ?? [])) {
        const elementosGatilho = buildElementosGatilho(gatilho);
        const payloadGatilho = {
          nome: gatilho.nome || `GTL-${obj.label}`,
          elementos: elementosGatilho,
        };
        let rtoGatilhoId = gatilho.rtoGatilhoId;
        if (!rtoGatilhoId) {
          // RTO recusa gatilho sem elemento — só cria quando há ao menos um válido.
          if (elementosGatilho.length === 0) continue;
          const gRes = await axios.post(`${urlMainAPI}/configurator/rto/gatilhos/${savedId}`, payloadGatilho);
          const gData = typeof gRes.data === 'string' ? JSON.parse(gRes.data) : gRes.data;
          rtoGatilhoId = gData?.data?.id != null ? String(gData.data.id) : null;
          const i2 = this.objectives.findIndex(o => o.id === obj.id);
          if (i2 !== -1 && rtoGatilhoId) {
            const o3 = this.objectives[i2];
            const gs = (o3.gatilhos || []).map(g => g.id === gatilho.id ? { ...g, rtoGatilhoId } : g);
            this.$set(this.objectives, i2, { ...o3, gatilhos: gs });
          }
        } else if (elementosGatilho.length > 0) {
          // Atualiza só com elementos — o RTO rejeita PUT de gatilho vazio.
          await axios.put(`${urlMainAPI}/configurator/rto/gatilhos/${savedId}/${rtoGatilhoId}`, payloadGatilho);
        }
        if (rtoGatilhoId) {
          const statusParam = gatilho.status === 'Ativo' ? 'Ativo' : 'Inativo';
          await axios.put(`${urlMainAPI}/configurator/rto/gatilhos/${savedId}/${rtoGatilhoId}/status?status=${statusParam}`);
        }
      }
    },

    async save() {
      if (this.objectives.length === 0) {
        showError('Adicione ao menos um objetivo antes de salvar.');
        return;
      }

      // Valida TODOS os objetivos antes de qualquer escrita — aborta o save inteiro se
      // algum tiver pendências, mostrando-as agrupadas por objetivo. Garante que o que está
      // na tela (em qualquer objetivo, selecionado ou não) seja persistido de fato.
      const comProblema = this.objectives
        .map(o => ({ nome: o.label || '(sem nome)', issues: this.getObjectiveValidationIssues(o) }))
        .filter(x => x.issues.length > 0);
      if (comProblema.length > 0) {
        this.showSaveIssues = true;
        // Toast enxuto: só resumo por objetivo. Os detalhes ficam no painel inline
        // (rto-save-issues) do objetivo selecionado, evitando uma mensagem gigante.
        const resumo = comProblema
          .map(x => `• ${x.nome}: ${x.issues.length} pendência${x.issues.length === 1 ? '' : 's'}`)
          .join('\n');
        const totalObj = comProblema.length;
        showError(`Não foi possível salvar — ${totalObj} objetivo${totalObj === 1 ? '' : 's'} com pendências:\n${resumo}\nSelecione o objetivo para ver os detalhes abaixo do botão Salvar.`);
        return;
      }
      this.showSaveIssues = false;

      this.isSaving = true;
      const sucessos = [];
      const erros = [];
      try {
        // Persiste cada objetivo sequencialmente. O estado de this.objectives é relido a cada
        // iteração (this.objectives.find), então POSTs que atribuem rtoOtimizacaoId via $set
        // são vistos pelas iterações seguintes. Iteramos sobre uma cópia (slice) por segurança.
        for (const objLocal of this.objectives.slice()) {
          const obj = this.objectives.find(o => o.id === objLocal.id);
          if (!obj) continue;
          try {
            await this.saveSingleObjective(obj);
            sucessos.push(obj.label || '(sem nome)');
          } catch (e) {
            console.error('Erro ao salvar objetivo RTO:', obj.label, e);
            const msg = e?.response?.data?.description ?? e?.response?.data?.message ?? e.message ?? 'erro desconhecido';
            erros.push({ nome: obj.label || '(sem nome)', msg });
          }
        }

        // Exclui no RTO os gatilhos que o usuário removeu da UI (de qualquer objetivo) — uma vez só.
        for (const rem of this.removedGatilhos) {
          try {
            await axios.delete(`${urlMainAPI}/configurator/rto/gatilhos/${rem.otimizacaoId}/${rem.rtoGatilhoId}`);
          } catch (err) {
            if (err?.response?.status !== 404) console.warn('Erro ao excluir gatilho RTO:', err?.response?.status);
          }
        }
        this.removedGatilhos = [];

        // Fase global — roda UMA vez ao final, só se pelo menos um objetivo persistiu.
        if (sucessos.length > 0) {
          // PUT /EstabilidadeGlobal hoje aceita só array de elementos.
          // stabilityName fica em memória local até o RTO expor suporte (ver RTO-PEDIDOS-IA.md).
          const estabilidadeElementos = this.stabilityCriteria
            .filter(s => s.corrente && s.propriedade && s.atributo)
            .map(s => this.mapMonitoringElement(s));
          await axios.put(`${urlMainAPI}/configurator/rto/estabilidade`, estabilidadeElementos);

          if (this.hasGlobalConfigPermission) {
            try { await this.saveConfigGlobal(); }
            catch (e) { console.warn('Erro ao salvar ConfiguracaoRTO global:', e); }
            // CA-08.3: persiste overrides de indicadores por objetivo
            await this.saveRtoConfigOverrides();
          }
        }

        if (erros.length === 0) {
          const msg = this.objectives.length === 1
            ? 'Configuração RTO salva com sucesso!'
            : `${sucessos.length} objetivos salvos com sucesso!`;
          defaultSuccess(msg);
          this.savedSuccess = true;
          setTimeout(() => { this.savedSuccess = false; }, 2000);
        } else {
          const detalhes = erros.map(e => `• ${e.nome}: ${e.msg}`).join('\n');
          showError(`${sucessos.length} de ${this.objectives.length} objetivo(s) salvos. Falhas:\n${detalhes}`);
        }
      } catch (e) {
        console.error('Erro ao salvar configuração RTO:', e);
        if (isRtoUnreachableError(e)) {
          showError(RTO_UNREACHABLE_MESSAGE);
        } else {
          const msg = e?.response?.data?.description ?? e?.response?.data?.message ?? e.message ?? 'Erro ao salvar.';
          showError(`Erro ao salvar configuração RTO: ${msg}`);
        }
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.rto-save-issues {
  margin: 8px 0;
  padding: 10px 12px;
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 6px;
  color: #78350f;
  font-size: 12px;
}
.rto-save-issues-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  color: #92400e;
}
.rto-save-issues-icon {
  flex-shrink: 0;
}
.rto-save-issues-list {
  margin: 0;
  padding-left: 20px;
  list-style: disc;
}
.rto-save-issues-list li {
  margin: 2px 0;
  line-height: 1.4;
}

.rto-drawer {
  position: absolute;
  top: 0;
  right: 0;
  width: 400px;
  height: calc(100% - 32px);
  background-color: #f9fafb;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.18);
  border-radius: 8px 0 0 8px;
  z-index: 100;
  overflow: hidden;
}

/* --- Drawer Header --- */
.rto-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #1f2937;
  color: #ffffff;
  padding: 12px 16px;
  flex-shrink: 0;
}

.rto-drawer-header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 600;
}

.rto-drawer-header-icon {
  color: #ff5f00;
  font-size: 1.1rem;
}

.rto-drawer-close {
  background: none;
  border: none;
  color: #ffffff;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 2px 4px;
  border-radius: 4px;
  transition: background-color 150ms;
}

.rto-drawer-close:hover {
  background-color: rgba(255, 255, 255, 0.15);
}

/* --- Body --- */
.rto-drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.rto-drawer-body::-webkit-scrollbar {
  width: 4px;
}

.rto-drawer-body::-webkit-scrollbar-track {
  background: transparent;
}

.rto-drawer-body::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 4px;
}

.rto-drawer-body::-webkit-scrollbar-thumb:hover {
  background-color: #ff5f00;
}

/* --- Cards area --- */
.rto-cards {
  padding: 14px 14px 4px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* --- Card --- */
.rto-card {
  border: 1px solid #fed7aa;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  background-color: #ffffff;
  transition: box-shadow 200ms ease, border-color 200ms ease;
}

.rto-card:hover {
  box-shadow: 0 4px 12px rgba(255, 95, 0, 0.12);
  border-color: #fdba74;
}

.rto-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #fff7ed;
  border-bottom: 1px solid #fed7aa;
  padding: 9px 14px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.rto-card-header--toggle {
  justify-content: space-between;
  width: 100%;
  border: none;
  border-bottom: 1px solid #fed7aa;
  cursor: pointer;
  transition: background-color 150ms;
}

.rto-card-header--toggle:hover {
  background-color: #ffedd5;
}

.rto-card-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rto-card-lock-icon {
  color: #ff5f00;
  font-size: 0.75rem;
}

.rto-card-chevron {
  color: #6b7280;
  font-size: 0.9rem;
  transition: transform 250ms ease;
}

.rto-card-header[aria-expanded="true"] .rto-card-chevron {
  transform: rotate(180deg);
}

.rto-card-body {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rto-card-body--relative {
  position: relative;
}

.rto-permission-overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(249, 250, 251, 0.88);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  z-index: 10;
  border-radius: 0 0 6px 6px;
  font-size: 0.72rem;
  color: #9ca3af;
  text-align: center;
  padding: 12px;
  backdrop-filter: blur(1px);
}

.rto-permission-overlay-icon {
  font-size: 1.4rem;
  color: #d1d5db;
}

/* --- Form fields --- */
.rto-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.rto-field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.rto-label {
  font-size: 0.72rem;
  color: #6b7280;
  margin: 0;
}

/* Ícone de ajuda (?) ao lado do label, com tooltip nativo */
.rto-label-help {
  margin-left: 4px;
  color: #b0b6c0;
  cursor: help;
  font-size: 0.78rem;
  vertical-align: -1px;
}
.rto-label-help:hover { color: #ff5f00; }

.rto-input {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 5px 8px;
  font-size: 0.82rem;
  color: #374151;
  background-color: #ffffff;
  transition: border-color 180ms ease, box-shadow 180ms ease;
}

.rto-input:focus {
  outline: none;
  border-color: #ff5f00;
  box-shadow: 0 0 0 3px rgba(255, 95, 0, 0.15);
}

.rto-input--error {
  border-color: #ef4444;
  box-shadow: 0 0 0 1px #ef4444;
}

.rto-field-error {
  font-size: 0.68rem;
  color: #ef4444;
  margin: 0;
}

.rto-field-hint {
  font-size: 0.65rem;
  color: #9ca3af;
  font-style: italic;
  margin-top: 2px;
}

/* --- Toggle (checkbox styled) --- */
.rto-field--toggle {
  flex-direction: column;
  gap: 2px;
}

.rto-toggle-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin: 0;
  user-select: none;
}

.rto-toggle-input {
  flex-shrink: 0;
  accent-color: #ff5f00;
  width: 14px;
  height: 14px;
  cursor: pointer;
}

.rto-toggle-text {
  font-size: 0.78rem;
  font-weight: 500;
  color: #374151;
}

/* --- Configuração Global (admin) — alinhado ao padrão dos demais cards --- */
.rto-config-global {
  margin-top: 12px;
  background-color: #ffffff;
  border: 1px solid #fed7aa;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rto-config-global-header {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #fff7ed;
  border-bottom: 1px solid #fed7aa;
  padding: 9px 14px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #374151;
}

.rto-config-global-icon {
  color: #ff5f00;
  font-size: 0.85rem;
}

.rto-config-global-title {
  flex: 1;
}

.rto-config-global-badge {
  display: inline-block;
  padding: 2px 7px;
  background-color: #ff5f00;
  color: white;
  font-size: 0.58rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-radius: 3px;
}

.rto-config-global-body {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rto-config-global-loading {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  font-size: 0.75rem;
  color: #6b7280;
}

/* CA-08.3: resumo de overrides + botão que abre o modal */
.phase3-overrides-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.phase3-no-overrides {
  font-size: 0.78rem;
  color: #6b7280;
  font-style: italic;
}

.phase3-override-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 10px;
  font-size: 0.72rem;
  font-weight: 600;
}

.phase3-override-badge--include {
  background-color: #dcfce7;
  color: #166534;
}

.phase3-override-badge--exclude {
  background-color: #fee2e2;
  color: #991b1b;
}

.phase3-open-modal-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 6px 10px;
  background-color: #ff5f00;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 150ms;
}

.phase3-open-modal-btn:hover {
  background-color: #e04e00;
}

.rto-modal-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid #e5e7eb;
}

/* CA-08.3: pill mostrando o objetivo atual (vem do radio do Operacional) */
.phase3-objective-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background-color: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 4px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #9a3412;
}

/* CA-08.3: lista de indicadores com tri-estado (auto/incluir/excluir) */
.phase3-indicators-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 6px;
  background-color: #fafafa;
}

.phase3-indicator-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 6px;
  background-color: #ffffff;
  border-radius: 3px;
}

.phase3-indicator-row:hover {
  background-color: #fff7ed;
}

.phase3-indicator-name {
  flex: 1;
  min-width: 0;
  font-size: 0.78rem;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.phase3-indicator-radios {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.phase3-indicator-radios label {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.7rem;
  color: #6b7280;
  cursor: pointer;
  margin: 0;
}

.phase3-indicator-radios input[type="radio"] {
  accent-color: #ff5f00;
  cursor: pointer;
}

.phase3-empty {
  text-align: center;
  padding: 12px;
  font-size: 0.75rem;
  color: #9ca3af;
}

/* --- Alerts --- */
.rto-alert {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 4px;
  font-size: 0.72rem;
}

.rto-alert-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.rto-alert--danger {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.rto-alert--warning {
  background-color: #fefce8;
  border: 1px solid #fde68a;
  color: #b45309;
}

/* --- Advanced buttons --- */
.rto-advanced-btns {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rto-config-action-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 7px 10px;
  font-size: 0.78rem;
  color: #374151;
  cursor: pointer;
  transition: background-color 150ms, transform 150ms, color 150ms;
}

.rto-config-action-btn:hover:not(:disabled) {
  background-color: #fff7ed;
  color: #ff5f00;
  transform: translateX(2px);
}

.rto-config-action-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* --- Footer --- */
.rto-drawer-footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
  background-color: #ffffff;
  flex-shrink: 0;
}

.rto-footer-row {
  display: flex;
  gap: 8px;
}

.rto-footer-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 7px 12px;
  background-color: #e5e7eb;
  color: #374151;
  border: none;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: background-color 150ms;
}

.rto-footer-btn:hover {
  background-color: #d1d5db;
}

.rto-footer-save-btn {
  width: 100%;
  padding: 9px;
  background-color: #ff5f00;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: background-color 150ms;
}

.rto-footer-save-btn:hover:not(:disabled) {
  background-color: #e04e00;
}

.rto-footer-save-btn--saved {
  background-color: #16a34a !important;
  transition: background-color 300ms ease;
}

.rto-footer-save-btn--saved:hover:not(:disabled) {
  background-color: #15803d !important;
}

.rto-footer-save-btn:active:not(:disabled) {
  transform: scale(0.97);
  transition: transform 80ms ease;
}

.rto-footer-save-btn:disabled {
  background-color: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
}

/* --- Badge nos botões de ação avançada --- */
.rto-action-btn-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #ff5f00;
  color: #fff;
  font-size: 0.62rem;
  font-weight: 700;
  border-radius: 10px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  margin-left: 6px;
  line-height: 0;
}

/* ══════════════════════════════════
   Modais: Critérios de Estabilidade
   e Gatilho para atuação do RTO
══════════════════════════════════ */

/* Search bar */
.scm-search-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 10px;
  margin-bottom: 10px;
  transition: border-color 180ms ease, box-shadow 180ms ease;
}

.scm-search-wrap:focus-within {
  border-color: #ff5f00;
  box-shadow: 0 0 0 3px rgba(255, 95, 0, 0.12);
  background-color: #ffffff;
}

.scm-search-icon {
  color: #9ca3af;
  font-size: 0.8rem;
  flex-shrink: 0;
  transition: color 180ms ease;
}

.scm-search-wrap:focus-within .scm-search-icon {
  color: #ff5f00;
}

.scm-search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 0.8rem;
  color: #374151;
  outline: none;
}

.scm-search-input::placeholder {
  color: #9ca3af;
}

.scm-search-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  transition: background-color 150ms, color 150ms;
}

.scm-search-clear:hover {
  background-color: #e5e7eb;
  color: #374151;
}

.scm-search-empty {
  text-align: center;
  font-size: 0.78rem;
  color: #9ca3af;
  padding: 12px 0;
}

.scm-field-error {
  font-size: 0.68rem;
  color: #ef4444;
  margin-top: 2px;
}

/* Name field (header dos modais) */
.scm-name-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 0 12px;
  border-bottom: 1px solid #f3f4f6;
  margin-bottom: 12px;
}

.scm-name-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin: 0;
}

.scm-toggle-inline {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: #374151;
  cursor: pointer;
  user-select: none;
  margin: 0;
}

.scm-toggle-inline input[type="checkbox"] {
  accent-color: #ff5f00;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  cursor: pointer;
}

/* Toggle "Pausar atuação": destaque visual + shine quando ligado */
.scm-toggle-pause {
  padding: 5px 8px;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: help;
  transition: background-color .2s ease, border-color .2s ease, box-shadow .2s ease, color .2s ease;
}
.scm-toggle-pause-icon { color: #9ca3af; font-size: 0.95rem; flex-shrink: 0; }
.scm-toggle-pause-help { color: #b0b6c0; font-size: 0.85rem; margin-left: 2px; flex-shrink: 0; }
.scm-toggle-pause:hover { border-color: #fcd34d; background-color: #fffbeb; }

.scm-toggle-pause--on {
  background-color: #fef3c7;
  border-color: #f59e0b;
  color: #92400e;
  font-weight: 600;
  animation: scm-pause-shine 1.8s ease-in-out infinite;
}
.scm-toggle-pause--on .scm-toggle-pause-icon { color: #d97706; }
.scm-toggle-pause--on .scm-toggle-pause-help { color: #d97706; }

@keyframes scm-pause-shine {
  0%, 100% { box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.18); }
  50%      { box-shadow: 0 0 0 5px rgba(245, 158, 11, 0.40); }
}

.scm-toggle-pause-hint {
  display: block;
  margin-top: 4px;
  font-size: 0.68rem;
  line-height: 1.3;
  color: #92400e;
}

.scm-pausado-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  background-color: #fef3c7;
  color: #92400e;
  font-size: 0.7rem;
  border-radius: 4px;
}

.scm-readonly-banner {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 9px 12px;
  margin-bottom: 10px;
  background-color: #fef3c7;
  border-left: 3px solid #f59e0b;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #92400e;
}

.scm-criterion-row--readonly {
  cursor: not-allowed !important;
  opacity: 0.85;
}

.scm-criterion-row--readonly:hover {
  background-color: transparent !important;
}

/* Empty state */
.scm-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 24px 0;
  color: #9ca3af;
  font-size: 0.8rem;
}

.scm-empty-icon {
  font-size: 2rem;
  color: #d1d5db;
}

/* Lista com scroll */
.scm-list {
  max-height: 220px;
  overflow-y: auto;
  margin-bottom: 8px;
  padding-right: 2px;
}

.scm-list::-webkit-scrollbar {
  width: 4px;
}

.scm-list::-webkit-scrollbar-track {
  background: transparent;
}

.scm-list::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 4px;
}

.scm-list::-webkit-scrollbar-thumb:hover {
  background-color: #ff5f00;
}

/* Criterion row (item da lista) */
.scm-criterion-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #f9fafb;
  margin-bottom: 6px;
  cursor: pointer;
  transition: border-color 150ms, background-color 150ms;
}

.scm-criterion-row:hover {
  border-color: #fdba74;
  background-color: #fff7ed;
}

.scm-criterion-row--editing {
  border-color: #ff5f00 !important;
  background-color: #fff7f0 !important;
}

.scm-criterion-info {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  flex-wrap: wrap;
}

.scm-criterion-tag {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 0.75rem;
  color: #374151;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scm-criterion-sep {
  color: #9ca3af;
  font-size: 0.7rem;
}

.scm-criterion-numbers {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  font-size: 0.7rem;
  color: #6b7280;
  white-space: nowrap;
}

.scm-remove-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #d1d5db;
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 0.8rem;
  transition: color 150ms, background-color 150ms;
  display: flex;
  align-items: center;
}

.scm-remove-btn:hover {
  color: #ef4444;
  background-color: #fef2f2;
}

/* Add form */
.scm-add-form {
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
  background: #f9fafb;
}

.scm-add-row--2col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 8px;
}

.scm-singleton-warning {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fefce8;
  border: 1px solid #fde68a;
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 0.72rem;
  color: #92400e;
  margin-bottom: 10px;
}

.scm-add-field--full {
  width: 100%;
  margin-bottom: 8px;
}

::v-deep .scm-searchable {
  font-size: 0.78rem;
}

.scm-add-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.scm-add-label {
  font-size: 0.68rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin: 0;
}

/* Coeficiente Angular display */
.scm-coef-display {
  display: flex;
  align-items: baseline;
  gap: 6px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 10px;
}

.scm-coef-label {
  font-size: 0.65rem;
  font-weight: 700;
  color: #c2410c;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.scm-coef-value {
  font-size: 1.2rem;
  font-weight: 700;
  font-family: monospace;
  color: #9a3412;
}

.scm-coef-unit {
  font-size: 0.7rem;
  font-weight: 700;
  color: #c2410c;
}

/* Add form actions */
.scm-add-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 4px;
}

/* "+ Adicionar" dashed button */
.scm-add-btn {
  width: 100%;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  background: none;
  color: #6b7280;
  font-size: 0.78rem;
  padding: 7px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: color 150ms, border-color 150ms, background-color 150ms;
  margin-top: 4px;
}

.scm-add-btn:hover {
  color: #ff5f00;
  border-color: #ff5f00;
  background-color: #fff7f0;
}

/* Footer do modal */
.scm-footer {
  display: flex;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid #e9ecef;
  margin-top: 10px;
}

.scm-btn--close-right {
  margin-left: auto;
}

.scm-btn.scm-btn--delete-all {
  background-color: transparent;
  color: #ef4444;
  border: 1px solid #ef4444;
}

.scm-btn.scm-btn--delete-all:hover {
  background-color: #fef2f2;
  border-color: #dc2626;
  color: #dc2626;
}


/* Botões genéricos */
.scm-btn {
  border: none;
  border-radius: 4px;
  padding: 5px 14px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: background-color 150ms;
}

.scm-btn--confirm {
  background-color: #ff5f00;
  color: #fff;
}

.scm-btn--confirm:hover:not(:disabled) {
  background-color: #e04e00;
}

.scm-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.scm-btn--cancel {
  background-color: #e5e7eb;
  color: #374151;
}

.scm-btn--cancel:hover {
  background-color: #d1d5db;
}

/* --- Sub-accordion (inside card body) --- */
.rto-sub-accordion {
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.rto-sub-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 7px 12px;
  background-color: #f9fafb;
  border: none;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #374151;
  cursor: pointer;
  transition: background-color 100ms;
}

.rto-sub-header:hover {
  background-color: #f3f4f6;
}

.rto-sub-header-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Modal de confirmação de ativação de objetivo */
.rto-activate-body {
  padding: 4px 2px 8px;
  font-size: 0.9rem;
  color: #374151;
}

.rto-activate-warning {
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  background-color: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  font-size: 0.84rem;
}

.rto-sub-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background-color: #ff5f00;
  color: #ffffff;
  font-size: 0.62rem;
  font-weight: 700;
  border-radius: 9px;
  letter-spacing: 0;
  text-transform: none;
  line-height: 1;
}

.rto-sub-body {
  padding: 8px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background-color: #ffffff;
}

/* --- Empty state --- */
.rto-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 24px 12px 16px;
  text-align: center;
}

.rto-empty-state-icon {
  font-size: 1.8rem;
  color: #d1d5db;
}

.rto-empty-state-title {
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
}

.rto-empty-state-subtitle {
  font-size: 0.68rem;
  color: #9ca3af;
  margin-bottom: 4px;
}

.rto-add-dashed-btn--empty {
  width: auto;
  padding: 5px 16px;
}

/* --- Add objective inline --- */
.rto-add-obj-nickname-row {
  display: flex;
  align-items: center;
  gap: 6px;
}


.rto-add-obj-input {
  flex: 1;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  padding: 4px 8px;
  font-size: 0.78rem;
  color: #374151;
}

.rto-add-obj-input:focus {
  outline: none;
  border-color: #ff5f00;
}

.rto-add-obj-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 3px;
  padding: 4px;
  font-size: 0.9rem;
  cursor: pointer;
}

.rto-add-obj-btn--confirm {
  color: #16a34a;
}

.rto-add-obj-btn--confirm:hover {
  background-color: #dcfce7;
}

.rto-add-obj-btn--cancel {
  color: #6b7280;
}

.rto-add-obj-btn--cancel:hover {
  background-color: #f3f4f6;
}

.rto-add-dashed-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 6px;
  background: none;
  border: 1px dashed #d1d5db;
  border-radius: 4px;
  font-size: 0.72rem;
  color: #9ca3af;
  cursor: pointer;
  transition: color 150ms, border-color 150ms;
}

.rto-add-dashed-btn:hover {
  color: #ff5f00;
  border-color: #ff5f00;
}

/* --- Modal header customizado --- */
::v-deep .rto-modal-header { padding: 0; border-bottom: none; }

.rto-modal-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #1f2937;
  color: #fff;
  border-bottom: 3px solid #ff5f00;
  padding: 10px 16px;
  width: 100%;
}

.rto-modal-header-title {
  display: flex;
  align-items: center;
  font-weight: bold;
  font-size: 0.95rem;
  gap: 8px;
}

.rto-modal-header-icon { color: #ff5f00; }

.rto-modal-header-close {
  background: none;
  border: none;
  color: #aaa;
  font-size: 1.1rem;
  cursor: pointer;
  line-height: 1;
}
.rto-modal-header-close:hover { color: #fff; }

/* --- List item transition --- */
.list-item-enter-active {
  transition: opacity 200ms ease, transform 200ms ease;
}
.list-item-leave-active {
  transition: opacity 150ms ease, transform 150ms ease;
}
.list-item-enter {
  opacity: 0;
  transform: translateY(-6px);
}
.list-item-enter-to {
  opacity: 1;
  transform: translateY(0);
}
.list-item-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* --- Card body slide transition --- */
.card-slide-enter-active,
.card-slide-leave-active {
  transition: max-height 280ms ease, opacity 200ms ease;
  overflow: hidden;
}

.card-slide-enter,
.card-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.card-slide-enter-to,
.card-slide-leave {
  max-height: 800px;
  opacity: 1;
}

/* --- Drawer slide transition --- */
.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 280ms ease, opacity 250ms ease;
}

.drawer-slide-enter,
.drawer-slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

</style>
